import React, { useState, useEffect } from 'react';
import {
    BookOpen, Search, ChevronDown, ChevronUp, X,
    PlusSquare, Activity, Users, Clock,
    ClipboardCheck, UserCheck, ArchiveRestore,
    LayoutDashboard, LogOut, CheckCircle, ShieldCheck,
    FileSpreadsheet, Landmark, FileSearch, Calculator, TrendingUp,
    CreditCard, QrCode, DollarSign, FileCheck2, PiggyBank,
    ShieldAlert, Percent, BarChart3, RefreshCcw, FileText, Wallet,
    Coins, Heart, UploadCloud,
    // Módulos e sub-módulos que entraram depois de 2026-06-12
    HardHat, Building2, Boxes, ShoppingCart, PackageCheck, GraduationCap,
    FileWarning,
    Award, CalendarX2, Scale, CalendarDays, Bell, Home, Gauge, Store,
    // Módulo Chamados (2026-08-21)
    LifeBuoy, PlusCircle, ListChecks, Monitor,
    // Módulo Biblioteca (2026-09-04)
    HelpCircle, Video,
    // Módulo Logística (2026-09-10)
    Truck, PackageOpen,
    // Módulo Marketing (2026-09-16)
    Megaphone, Palette, Cake
} from 'lucide-react';

// ============================================================================
// Central de Ajuda (Manual da Tela)
//
// Como funciona o roteamento dos verbetes:
//  - Cada MÓDULO tem seu próprio array de verbetes (manualFiscal, manualRh, ...).
//  - O casco do módulo abre o manual passando `modulo="<nome>"`; aí só aparecem
//    os verbetes daquele módulo. O `secaoInicial` define qual verbete já abre
//    expandido (normalmente o da aba ativa — ver getSecaoAjuda em cada casco).
//  - As telas-ferramenta embutidas (Apuração, Auditoria Fiscal, P&L, etc.) NÃO
//    têm botão de ajuda próprio quando embutidas — quem mostra o manual é o botão
//    do casco. Por isso o array do módulo precisa conter TODOS os verbetes das
//    ferramentas que vivem nele.
//  - `showOnlyIds` filtra para um ou poucos verbetes — usado quando uma tela é
//    aberta fora do casco e quer mostrar só o seu verbete. Sem `modulo`, cai no
//    "junta tudo" (else lá embaixo), então todo id usado em showOnlyIds precisa
//    existir em ALGUM array — e os ids têm de ser ÚNICOS entre os arrays.
// ============================================================================
const HelpCenter = ({ isOpen, onClose, secaoInicial, showOnlyIds, modulo }) => {
    const [secaoAtiva, setSecaoAtiva] = useState(null);
    const [termoBusca, setTermoBusca] = useState('');

    useEffect(() => {
        if (isOpen && secaoInicial) {
            setSecaoAtiva(secaoInicial);
            setTermoBusca('');
        }
    }, [isOpen, secaoInicial]);

    if (!isOpen) return null;

    const toggleSecao = (id) => {
        setSecaoAtiva(secaoAtiva === id ? null : id);
    };

    // ----------------------------------------------------------------------
    // 1. MÓDULO DE FORMULÁRIOS
    // ----------------------------------------------------------------------
    // Módulo Treinamento & Qualidade (ex-"Central de Formulários", seção de qualidade).
    const manualTreinamento = [
        {
            id: 'visao-geral',
            icon: LayoutDashboard,
            title: 'Treinamento & Qualidade: Visão Geral',
            color: '#0284c7',
            desc: 'Como navegar pelo módulo de Treinamento & Qualidade.',
            content: (
                <div>
                    <p style={pStyle}>O módulo de <strong>Treinamento &amp; Qualidade</strong> tem <strong>dois cards</strong>:</p>
                    <ul style={listStyle}>
                        <li><strong>Formulários:</strong> a <strong>Avaliação de Treinamento</strong> (ex-Avaliação TSV; duas ações: <em>Preencher</em> e <em>Relatórios</em>) e a <strong>Auditoria SA Quiosque</strong>, descontinuada em 2026-09-23 — só <em>Histórico</em>, sem preenchimento (a checagem virou parte da área Quiosque da Avaliação 360). ⚠️ A <strong>Auditoria SA</strong> mudou para o módulo Operacional no mesmo dia (virou "Avaliação de Segurança do Alimento").</li>
                        <li><strong>PPI — Promoção Interna:</strong> o programa de promoção interna de ponta a ponta (processos e vagas, inscrição por link, triagem, prova com ranking e LIVE).</li>
                    </ul>
                    <p style={pStyle}>Quem enxerga o quê: o card de <strong>Formulários</strong> é do Treinamento; o do <strong>PPI</strong> é dividido com o <strong>RH</strong> — e dentro do PPI cada perfil vê só as suas abas.</p>
                </div>
            )
        },
        {
            // Renomeada de "Avaliação TSV" para "Avaliação de Treinamento" em
            // 2026-09-23 (DEM-00011) — rótulo apenas, id/tabela/rota seguem os
            // mesmos (`avaliacao-tsv`, `dtbAvaliacaoTSV`, `/api/avaliacao`).
            id: 'avaliacao-tsv',
            icon: CheckCircle,
            title: 'Formulários: Avaliação de Treinamento',
            color: '#0284c7',
            desc: 'Avaliação técnica dos padrões de serviço no restaurante.',
            content: (
                <div>
                    <p style={pStyle}>A <strong>Avaliação de Treinamento</strong> (ex-Avaliação TSV) era a gêmea da Auditoria SA — mesmo jeito de preencher, mesmas regras de evidência e mesmo PDF. Segue em uso no dia a dia; a SA mudou para o módulo Operacional em 2026-09-23.</p>
                    <p style={pStyle}><strong>Como Avaliar:</strong></p>
                    <ol style={listStyle}>
                        <li>Clique em <strong>"Preencher"</strong> no card de Avaliação de Treinamento.</li>
                        <li>Informe a loja, a data e os gerentes responsáveis pela visita.</li>
                        <li>Responda o questionário. Toda resposta <strong>"Não" exige foto e observação</strong> (1 obrigatória + até 2 extras).</li>
                        <li>Confira o resumo com a pontuação e envie. O <strong>PDF da visita</strong> é gerado com as fotos e guardado no SharePoint.</li>
                    </ol>
                    <p style={pStyle}><strong>Desempenho Geral (Relatório):</strong></p>
                    <ul style={listStyle}>
                        <li>Acompanhe as notas por seção e a evolução das lojas nos indicadores do painel.</li>
                        <li>Use <strong>"Ver PDF"</strong> para abrir o relatório da visita e <strong>"Exportar CSV"</strong> para baixar a planilha.</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'ck-quiosque',
            icon: Store,
            title: 'Formulários: Auditoria SA Quiosque (histórico)',
            color: '#0284c7',
            desc: 'Descontinuada — segurança dos Dessert Centers (ilhas e quiosques), absorvida pela área Quiosque da Avaliação 360.',
            content: (
                <div>
                    <p style={pStyle}><strong>Descontinuada em 2026-09-23</strong> — a checagem dos <strong>Dessert Centers</strong> (as ilhas/CK) virou parte da área <strong>Quiosque</strong> da Avaliação 360 (módulo Operacional). Este card não tem mais "Preencher": só <strong>Histórico</strong>, com os registros já gravados (34 visitas) e seus PDFs — nada foi apagado.</p>
                    <p style={pStyle}>Cada visita antiga tinha <strong>19 perguntas</strong> em três seções — <strong>Controles</strong> (5), <strong>Operacional</strong> (9) e <strong>Margem Bruta</strong> (5) —, cada uma valendo 1 ponto, resposta <strong>CF</strong> (conforme) ou <strong>NC</strong> (não conforme, sem N/A).</p>
                    <div style={{ backgroundColor: '#f0f9ff', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #0284c7', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#075985', margin: 0 }}>
                            <strong>O resultado era pontuação, não veredito.</strong> O formulário de papel não definia faixa de aprovação, então o PDF mostra o placar (ex.: <em>18/19 pontos</em>) sem dizer "Aprovado" ou "Reprovado".
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'ppi-processos',
            icon: GraduationCap,
            title: 'PPI: Processos, Vagas e Triagem',
            color: '#0284c7',
            desc: 'Abertura do processo, inscrição por link e triagem dos candidatos.',
            content: (
                <div>
                    <p style={pStyle}>O <strong>PPI (Programa de Promoção Interna)</strong> é um sub-hub com abas na ordem real do processo. O RH opera as três primeiras; o Treinamento, as de prova e LIVE.</p>
                    <p style={pStyle}><strong>Processos &amp; Vagas:</strong></p>
                    <ol style={listStyle}>
                        <li>Crie o <strong>processo</strong> (o código é a data, ex.: <em>PPI-2026-08-05</em>) e as <strong>vagas</strong> — cargo e local saem de listas prontas.</li>
                        <li>Defina o <strong>público-alvo</strong> em três seletores que se cruzam (Lojas · Centros de lucro · Cargos): escolher um filtra os outros. A <strong>prévia de destinatários</strong> mostra quem vai receber antes de publicar.</li>
                        <li><strong>Publique</strong>. O sistema enfileira o WhatsApp com o <strong>link/QR de inscrição</strong>; o funcionário se inscreve informando o CPF.</li>
                        <li>Acompanhe o <strong>card de Envios</strong>: <em>Enviados</em>, <em>Na fila</em>, <em>Não enviados</em> (celular errado ou ausente no cadastro — quem corrige é o RH, na origem) e <em>Com erro</em> (o ManyChat recusou).</li>
                    </ol>
                    <p style={pStyle}><strong>Triagem:</strong> os inscritos aparecem por vaga já com <strong>faltas e atestados</strong> lidos do espelho de ponto (só leitura). Aprove ou reprove e dispare os avisos.</p>
                    <p style={pStyle}><strong>Candidato que desistiu:</strong> use o link <strong>"desistiu"</strong> na linha dele e informe o <strong>motivo</strong> (obrigatório) — não o reprove por isso. Reprovado e desistente são coisas diferentes, e misturá-los distorce o aproveitamento da edição. Ele continua na lista, marcado em cinza, e sai da triagem, da chamada da sala e da fila de avaliação; dá para <strong>desfazer</strong> se tiver sido engano. O mesmo link existe nas abas de <strong>Aplicação</strong> e <strong>LIVE</strong> — e o total de desistências do processo aparece aqui, acima das vagas.</p>
                    <div style={{ backgroundColor: '#fffbeb', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #f59e0b', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                            <strong>Cuidado com o reenvio.</strong> Reenviar manda a mesma mensagem de novo — cada envio é cobrado e consome a cota de entrega da pessoa. Se a mensagem não chegou mas o placar diz "Enviado", <strong>espere 24h</strong> em vez de reenviar: insistir reforça o bloqueio do WhatsApp.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'ppi-prova',
            icon: FileCheck2,
            title: 'PPI: Banco de Questões, Provas e Ranking',
            color: '#0284c7',
            desc: 'Monta a prova sorteada, aplica na sala e gera o ranking.',
            content: (
                <div>
                    <p style={pStyle}><strong>Banco de questões:</strong> as questões pertencem a uma <strong>trilha</strong> de promoção (ex.: Atendente → Treinador) e são organizadas por <strong>área × nível</strong>. Dá para importar por planilha (Admin/Diretoria), desativar e — só se a questão <em>nunca caiu em prova</em> — excluir.</p>
                    <p style={pStyle}><strong>Provas:</strong> a prova é um <strong>sorteio</strong> do banco, em dois modos: quantidade por área ou percentual por nível. Depois de sortear, dá para trocar uma questão escolhendo qual entra, corrigir gabarito, anular e publicar. O gabarito fica <strong>congelado</strong> na prova.</p>
                    <p style={pStyle}><strong>Aplicação &amp; Ranking (o dia da prova):</strong></p>
                    <ol style={listStyle}>
                        <li>Na tabela de vagas do processo, vincule cada vaga a uma prova publicada.</li>
                        <li>Clique em <strong>Gerar QR Code</strong>: abre a tela de <strong>projeção</strong> para o telão — relógio e o <strong>QR único da sala</strong>. O candidato escaneia, informa o CPF e o sistema o manda para a prova da <em>sua</em> trilha.</li>
                        <li>Marque a <strong>presença</strong> linha a linha ou use <strong>"Marcar todos presentes"</strong>. Sem presença marcada, a prova não abre para o candidato.</li>
                        <li>A prova <strong>salva sozinha</strong> cada resposta. Ao fim, <strong>Encerrar prova</strong> fecha a sala e registra como entregue (com o parcial) quem estava presente e não enviou.</li>
                        <li>O <strong>ranking</strong> sai na hora: nota de 0 a 10, selo de <strong>apto (≥ 7)</strong>, tempo de empresa como desempate e a decisão do RH. Clicar no nome abre a <strong>prova corrigida</strong>. Dá para baixar em PDF.</li>
                    </ol>
                    <p style={pStyle}>As alternativas são <strong>embaralhadas por candidato</strong> e a prova não mostra a área de cada questão — é o que impede chutar pelo padrão do gabarito.</p>
                    <p style={pStyle}><strong>Quem não apareceu e avisou que saiu:</strong> registre pelo link <strong>"desistiu"</strong> na linha, com o motivo. Ele deixa de ser chamado para a sala e some da conta de "marcar todos presentes", mas <strong>continua no ranking, com a nota e a posição que fez</strong> — se o primeiro colocado desistiu, você vê isso e convoca o segundo.</p>
                </div>
            )
        },
        {
            id: 'ppi-live',
            icon: ClipboardCheck,
            title: 'PPI: LIVE (avaliação de chão) e Histórico',
            color: '#0284c7',
            desc: 'A etapa prática que fecha o PPI, 30 dias após a prova.',
            content: (
                <div>
                    <p style={pStyle}>O <strong>LIVE</strong> é a avaliação prática no chão de loja, feita cerca de <strong>30 dias após a prova</strong> — é ela que fecha o PPI. Duas sub-abas:</p>
                    <ul style={listStyle}>
                        <li><strong>Candidatos a avaliar:</strong> a lista de quem está nessa etapa e o formulário de avaliação.</li>
                        <li><strong>Formulários:</strong> o construtor de versões — o Treinamento monta as áreas e as perguntas pela própria tela. A versão <strong>congela ao ser usada</strong>; para mudar, duplique.</li>
                    </ul>
                    <p style={pStyle}>O score é de <strong>conformidade</strong> (itens marcados N/A ficam fora da conta), mas <strong>a aprovação é humana</strong> — não existe nota de corte automática. Os 30 dias são sugestão, não trava: o botão de avaliar funciona sempre.</p>
                    <p style={pStyle}><strong>Desistência:</strong> o link <strong>"desistiu"</strong> na linha registra que o candidato saiu do processo (com motivo obrigatório). O card <strong>Desistiram</strong> conta os desta etapa; o total do processo fica na aba <strong>Triagem</strong>, que é a única que lista todos os inscritos.</p>
                    <p style={pStyle}><strong>Histórico:</strong> uma linha por candidato com todas as etapas percorridas, com filtros e impressão da <strong>lista de aprovados</strong> em PDF ou Excel. Quem desistiu aparece como <strong>"Desistiu"</strong> na etapa final, e sai marcado na lista impressa.</p>
                </div>
            )
        },
    ];

    // Módulo Medicina do Trabalho (ex-"Central de Formulários", seção de medicina).
    const manualMedicina = [
        {
            id: 'atestado-medico',
            icon: ClipboardCheck,
            title: 'Medicina do Trabalho: Revisão de Atestados',
            color: '#16a34a',
            desc: 'Lançamento dos atestados pelo médico e consulta da redução de dias.',
            content: (
                <div>
                    <p style={pStyle}>O módulo tem um sub-módulo, <strong>Revisão de Atestados</strong>, com <strong>duas telas</strong>. Quem lança é o médico; RH, gerência e diretoria entram direto na Consulta.</p>
                    <p style={pStyle}><strong>Aba "Lançamento" (o médico registra):</strong></p>
                    <ol style={listStyle}>
                        <li>Localize o colaborador e informe a <strong>data do atestado</strong> e os <strong>dias apresentados</strong>.</li>
                        <li>Registre os <strong>dias concedidos</strong> após a revisão médica — a diferença é a redução.</li>
                        <li>Salve. Cada documento entregue é um registro próprio (duas entregas no mesmo dia são duas linhas).</li>
                    </ol>
                    <p style={pStyle}><strong>Aba "Consulta" (os números):</strong></p>
                    <ul style={listStyle}>
                        <li>Os dados abrem em árvore: <strong>Loja → Cargo → Nome → Atestado</strong>, clicando para descer nível a nível.</li>
                        <li>As medidas vêm <strong>em par</strong>: o <strong>Total</strong> e os <strong>&gt; 1 dia</strong> — atestado de um dia só é difícil de atuar, então ele é separado.</li>
                        <li>Escolha o <strong>critério do período</strong>: <em>data do atestado</em> (leitura de gestão — o mês não muda depois) ou <em>data do lançamento</em> (o trabalho de revisão do médico). Os totais mudam conforme o critério, porque na maioria dos registros as duas datas são diferentes.</li>
                    </ul>
                    <div style={{ backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #16a34a', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#166534', margin: 0 }}>
                            Esta consulta substitui o antigo relatório linha a linha <strong>e</strong> a página "Revisões Atestados" do Power BI — o portal passou a ser a fonte única.
                        </p>
                    </div>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // 2. MÓDULO DE PROCESSO SELETIVO
    // ----------------------------------------------------------------------
    const manualProcessoSeletivo = [
        {
            id: 'gestao-vagas',
            icon: PlusSquare,
            title: 'Processo Seletivo: Gestão de Vagas',
            desc: 'Como criar, editar e publicar vagas no site.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como Criar ou Editar uma Vaga:</strong></p>
                    <ol style={listStyle}>
                        <li><strong>Nova Requisição:</strong> Acesse a aba "Gestão de Vagas" e preencha o formulário de publicação.</li>
                        <li><strong>Preencha os Detalhes:</strong> Selecione o cargo (alimentado pelo banco de dados), informe salário, escalas (ex: 12x36), turnos e requisitos.</li>
                        <li><strong>Publicação:</strong> Clique em <strong>"Publicar Vaga no Site"</strong>. A vaga aparecerá na rota pública de inscrições.</li>
                        <li><strong>Ocultar/Inativar:</strong> Para retirar do ar, clique no ícone de "olho" na lista "No Site Agora". A vaga irá para "Ocultas / Rascunhos", onde pode ser editada.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'monitoramento',
            icon: Activity,
            title: 'Processo Seletivo: Monitoramento',
            desc: 'Visão geral e cruzamento de dados de todos os candidatos.',
            content: (
                <div>
                    <p style={pStyle}>Este módulo cruza automaticamente as informações de inscrição via CPF.</p>
                    <ul style={listStyle}>
                        <li><strong>Filtros Dinâmicos:</strong> Use as barras de pesquisa no topo para filtrar por Nome, Vaga ou Status Detalhado (ex: "Exame de Admissão").</li>
                        <li><strong>Exportação:</strong> Clique em <strong>"Exportar Excel"</strong> para baixar a lista atual filtrada em formato .csv.</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'inscritos',
            icon: Users,
            title: 'Processo Seletivo: Inscritos (Triagem - Etapa 1)',
            desc: 'Triagem inicial dos currículos recém-chegados.',
            content: (
                <div>
                    <ol style={listStyle}>
                        <li><strong>Análise:</strong> Verifique os dados do candidato, como "Vaga de Interesse" e "Regiões Disponíveis".</li>
                        <li><strong>Aprovar:</strong> Clique no botão verde <strong>"Aprovar"</strong> para confirmar o currículo. O candidato será movido para o Agendamento de Entrevistas.</li>
                        <li><strong>Reprovar:</strong> Clique no botão vermelho <strong>"Reprovar"</strong> para arquivar o currículo no Banco de Talentos.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'agendamento',
            icon: Clock,
            title: 'Processo Seletivo: Entrevistas (Etapas 2 e 3)',
            desc: 'Gestão de agendamentos e registro do parecer.',
            content: (
                <div>
                    <p style={pStyle}><strong>Aba: Fila de Agendamento</strong></p>
                    <ul style={listStyle}>
                        <li><strong>Agendar:</strong> Selecione o Local (ex: FER, MOX) e a Data disponível, e clique em "Agendar".</li>
                        <li><strong>Agendamento em Lote:</strong> Marque os candidatos, escolha Local/Data na barra superior e clique em "Agendar Lote".</li>
                        <li><strong>Contatar:</strong> Caso não consiga falar com o candidato, clique em "Contatar" para marcá-lo como "Aguardando Retorno".</li>
                    </ul>
                    <p style={pStyle}><strong>Aba: Aguardando Parecer</strong></p>
                    <ul style={listStyle}>
                        <li><strong>Aprovar (Passo a Passo):</strong> Preencha a Loja, Grupo, Cargo, Escala e Folga e confirme a aprovação. O candidato segue direto para a etapa de <strong>Integração</strong> (aba Aprovados).</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'aprovados',
            icon: UserCheck,
            title: 'Processo Seletivo: Integração/Aprovados',
            desc: 'Agendamento do primeiro dia na loja.',
            content: (
                <div>
                    <ol style={listStyle}>
                        <li>Na aba "Aguardando Início", clique em <strong>"Finalizar Processo"</strong>.</li>
                        <li>Preencha a "Data do 1º Dia", "Horário de Chegada" e o "Endereço da Loja".</li>
                        <li>Clique em "Finalizar Processo e Enviar" para disparar o e-mail de boas-vindas.</li>
                        <li>Se o candidato faltar, use <strong>"Faltou / Remarcar"</strong> para definir nova data.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'historico',
            icon: ArchiveRestore,
            title: 'Processo Seletivo: Desistentes e Reprovados',
            desc: 'Gestão do banco de talentos e repescagem.',
            content: (
                <div>
                    <ul style={listStyle}>
                        <li><strong>Desistentes:</strong> Use o botão <strong>"Restaurar"</strong> para aplicar uma repescagem e devolver à Fila de Entrevistas. O robô enviará uma nova mensagem via WhatsApp.</li>
                        <li style={{ marginTop: '12px' }}>
                            <strong>Reprovados:</strong> Consulte o histórico e exporte a lista em Excel (XLSX) para vagas futuras. Entenda as definições dos motivos de reprovação:
                            <ul style={{ ...listStyle, marginTop: '8px', paddingLeft: '20px', fontSize: '13px', color: '#475569' }}>
                                <li style={{ marginBottom: '6px' }}><strong>Menor de idade:</strong> Bloqueio automático na triagem inicial. Ocorre quando a idade calculada pela data de nascimento não atende aos requisitos mínimos da vaga (ex: menor de 14 anos para Aprendiz ou menor de 18 para vagas regulares).</li>
                                <li style={{ marginBottom: '6px' }}><strong>Reprovação por Histórico:</strong> Bloqueio aplicado na triagem ou internamente devido a restrições no cruzamento de CPF (ex: ex-funcionários não recontratáveis).</li>
                                <li style={{ marginBottom: '6px' }}><strong>Não se adequa a cultura da empresa:</strong> Reprovação manual aplicada pela Gerência ou pelo RH após a realização da entrevista presencial.</li>
                                <li style={{ marginBottom: '6px' }}><strong>Não compareceu ao Exame Admissional:</strong> O candidato foi aprovado na entrevista com o gerente, mas faltou na etapa médica ou na entrega do uniforme/integração.</li>
                                <li><strong>Reprovação Automática - Prazo Expirado (21 dias):</strong> O sistema encerra automaticamente candidatos que ficaram inativos. Isso inclui pessoas que pararam na triagem, não responderam ao agendamento no WhatsApp ou abandonaram o processo no meio do caminho há mais de 21 dias.</li>
                            </ul>
                        </li>
                    </ul>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // 3. MÓDULO DE DADOS & INTEGRAÇÕES (telas ocultas, sem rota — mas com manual)
    // ----------------------------------------------------------------------
    const manualDados = [
        {
            id: 'consolidado-sovos',
            icon: FileSpreadsheet,
            title: 'Dados: Consolidador Sovos',
            color: '#0284c7',
            desc: 'Unificação e tratamento de múltiplos relatórios.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como utilizar o Consolidador Sovos:</strong></p>
                    <ol style={listStyle}>
                        <li>Acesse o módulo <strong>Dados &amp; Integrações</strong> e clique em "Iniciar Ferramenta" no cartão do Consolidador Sovos.</li>
                        <li><strong>Upload de Ficheiros:</strong> Na nova ecrã, carregue todos os ficheiros CSV que extraiu do sistema Sovos e que deseja unificar (pode selecionar vários ao mesmo tempo).</li>
                        <li><strong>Processamento:</strong> O sistema irá ler todos os ficheiros e tratar automaticamente os dados. As colunas de Valores e Alíquotas (que começam por <em>VL_</em> ou <em>AL_</em>) serão formatadas corretamente como números.</li>
                        <li><strong>Download:</strong> Após a conclusão, o sistema fará automaticamente o download de um único ficheiro CSV consolidado e padronizado, pronto para uso em Excel, PowerBI ou outra ferramenta de análise.</li>
                    </ol>
                    <div style={{ backgroundColor: '#fffbeb', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #f59e0b', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                            <strong>Aviso:</strong> Certifique-se de que os ficheiros enviados estão no formato original (separados por ponto e vírgula e com codificação UTF-8) para evitar erros de leitura.
                        </p>
                    </div>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // 4. MÓDULO FISCAL (roxo)
    // ----------------------------------------------------------------------
    const manualFiscal = [
        {
            id: 'visao-geral-fiscal',
            icon: ShieldAlert,
            title: 'Fiscal: Visão Geral',
            color: '#7c3aed',
            desc: 'Como navegar pelo Módulo Fiscal.',
            content: (
                <div>
                    <p style={pStyle}>O Módulo Fiscal reúne as ferramentas tributárias do grupo, organizadas em duas abas:</p>
                    <ul style={listStyle}>
                        <li><strong>Automações:</strong> Apuração de Impostos (ICMS, DIFAL, PIS/COFINS) e Auditoria Fiscal (SPED × SAP/Sovos). A Auditoria Fiscal inclui a sub-aba <strong>Verificação de Alíquotas</strong> de PIS/COFINS.</li>
                        <li><strong>Relatórios:</strong> Fechamento Mensal na visão fiscal e <strong>Redução do Benefício (CST 06)</strong> — consulta na tela e exportação em Excel.</li>
                    </ul>
                    <p style={pStyle}>Os verbetes abaixo trazem o passo a passo de cada ferramenta.</p>
                </div>
            )
        },
        {
            id: 'apuracao-impostos',
            icon: Landmark,
            title: 'Fiscal: Apuração ICMS e PIS/COFINS',
            color: '#7c3aed',
            desc: 'Geração de guias em cascata (ICMS, DIFAL, Biomarca e PIS/COFINS).',
            content: (
                <div>
                    <p style={pStyle}><strong>Como realizar a Apuração Fiscal:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta de Apuração de Impostos.</li>
                        <li><strong>Parâmetros:</strong> Selecione o Mês/Ano de competência e a Empresa desejada para gerar o cálculo.</li>
                        <li><strong>Processamento:</strong> O sistema conectará automaticamente ao banco de dados e aplicará as regras de cálculo tributário em cascata (ICMS Padrão, DIFAL e Biomarca, além da visão do PIS/COFINS Misto).</li>
                        <li><strong>Resultado:</strong> Será feito o download de um ficheiro Excel (.xlsx) já formatado. O arquivo contém uma <em>Capa de Resumo</em> (com os totais consolidados) e abas detalhadas com as bases de cálculo extraídas do Sovos e Biomarca.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'auditoria-fiscal',
            icon: FileSearch,
            title: 'Fiscal: Auditoria Fiscal (SPED vs SAP)',
            color: '#7c3aed',
            desc: 'Cruzamento massivo de dados de Movimento Fiscal.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como realizar a Auditoria:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta de Auditoria Fiscal.</li>
                        <li><strong>Análise de Divergências:</strong> O sistema irá cruzar milhares de linhas entre a base do SAP (Movimento Fiscal) e a base do Sovos (SPED).</li>
                        <li><strong>Faltas e Sobras:</strong> A ferramenta identifica com precisão cirúrgica os dias em que houve <em>"Falta"</em> (registros presentes no SAP, mas ausentes no Sovos) e <em>"Sobra"</em> (registros no Sovos não contabilizados no SAP).</li>
                        <li><strong>Relatório e Controle:</strong> No final do processamento, você receberá um relatório com o detalhamento das divergências. O sistema também grava automaticamente o status da auditoria no banco de dados para o histórico de controle da loja.</li>
                        <li><strong>Reprocessamento:</strong> Existe uma trava para não auditar período já classificado como Base 100% correta — o aplicativo não refaz a auditoria se o relatório de Análise de Movimento Fiscal já tiver períodos auditados.</li>
                    </ol>
                    <div style={{ backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #16a34a', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#166534', margin: 0 }}>
                            <strong>Dica:</strong> Se não houver divergências e as bases baterem 100%, o sistema notificará que a conciliação foi perfeita e não será necessário baixar relatórios de erros.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'verificacao-aliquotas',
            icon: Percent,
            title: 'Fiscal: Verificação de Alíquotas (PIS/COFINS)',
            color: '#7c3aed',
            desc: 'Audita base e imposto de PIS/COFINS na Sovos, por igualdade exata.',
            content: (
                <div>
                    <p style={pStyle}>Fica em <strong>Automações → Auditoria Fiscal → aba "Verificação de Alíquotas"</strong>. Audita o PIS/COFINS lançado na base Sovos (<em>dtbSovosItens</em>) das 4 empresas, sempre filtrado por período e CNPJs.</p>
                    <p style={pStyle}><strong>Duas verificações:</strong></p>
                    <ol style={listStyle}>
                        <li><strong>CST 06</strong> (alíquota zero): a base de cálculo (<em>VL_BASE_CALCULO</em>) tem de estar <strong>zerada</strong>. Qualquer valor diferente de zero é sinalizado.</li>
                        <li><strong>CST 01</strong> (tributado): a base correta é <em>VL_TOTAL − VL_DESCONTO − VL_ICMS</em>, e o imposto correto é <em>base × alíquota do regime</em>:
                            <ul style={{ ...listStyle, marginTop: '8px' }}>
                                <li><strong>Lucro Real:</strong> PIS 1,65% · COFINS 7,60%</li>
                                <li><strong>Lucro Presumido:</strong> PIS 0,65% · COFINS 3,00%</li>
                                <li><strong>Hunger:</strong> passou a Lucro Real em <strong>01/01/2026</strong> — o regime aplicado depende da data da nota.</li>
                            </ul>
                        </li>
                    </ol>
                    <p style={pStyle}>A comparação entre o valor lançado (<em>VL_PIS</em>/<em>VL_COFINS</em>) e o valor correto é por <strong>igualdade exata</strong> — não há tolerância: qualquer diferença, por menor que seja, aparece. A tela devolve <strong>só as divergências</strong>, já com os valores corretos de PIS, COFINS e a soma.</p>
                </div>
            )
        },
        {
            id: 'fechamento-fiscal',
            icon: BarChart3,
            title: 'Fiscal: Fechamento Mensal',
            color: '#7c3aed',
            desc: 'Faturamento e impostos consolidados do mês, visão fiscal.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como extrair o Fechamento (visão Fiscal):</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Relatórios</strong>, abra o cartão "Fechamento Mensal (Fiscal)".</li>
                        <li><strong>Parâmetros:</strong> Selecione o período. A visão já é a fiscal (não precisa escolher).</li>
                        <li><strong>Processamento:</strong> O relatório consulta diretamente o banco — sem upload de planilhas — e consolida faturamento e impostos por grupo de loja.</li>
                        <li><strong>Resultado:</strong> Visualize na tela e clique em <strong>Exportar Excel</strong>.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'reducao-beneficio',
            icon: Percent,
            title: 'Fiscal: Redução do Benefício (CST 06)',
            color: '#7c3aed',
            desc: 'Confere o benefício de PIS (alíquota zero) nas NCMs beneficiadas.',
            content: (
                <div>
                    <p style={pStyle}>Na aba <strong>Relatórios</strong>. Confere o <strong>benefício fiscal de PIS</strong> (alíquota zero, CST 06) nas vendas das NCMs beneficiadas — leite e derivados e águas/bebidas — direto na base Sovos.</p>
                    <ol style={listStyle}>
                        <li>Informe o <strong>período</strong> (obrigatório).</li>
                        <li>Escolha a <strong>empresa</strong> — obrigatória e <strong>uma de cada vez</strong>: somar as quatro infla o resultado.</li>
                        <li>Consulte na tela (com os totais no topo) e use <strong>Exportar CSV</strong> para levar para o Excel.</li>
                    </ol>
                    <p style={pStyle}>O resultado sai agrupado por <strong>loja + NCM + dia</strong>, com CST de PIS e COFINS, valor total e desconto.</p>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // 5. MÓDULO CONTÁBIL (verde)
    // ----------------------------------------------------------------------
    const manualContabil = [
        {
            id: 'visao-geral-contabil',
            icon: Calculator,
            title: 'Contábil: Visão Geral',
            color: '#16a34a',
            desc: 'Como navegar pelo Módulo Contábil.',
            content: (
                <div>
                    <p style={pStyle}>O Módulo Contábil reúne as ferramentas de controle e análise contábil:</p>
                    <ul style={listStyle}>
                        <li><strong>RTB12 Simples Nacional:</strong> controle do limite, rolling forecast e rateio de notas.</li>
                        <li><strong>Automações:</strong> Análise de P&amp;L (Z-Score) e Conferência de Vendas (Balancete × SOVOS).</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'rtb12-simplesnacional',
            icon: DollarSign,
            title: 'Contábil: RTB12 Simples Nacional',
            color: '#16a34a',
            desc: 'Controle do limite do Simples Nacional, rateio orçado e rolling forecast.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como utilizar a Análise RTB12:</strong></p>
                    <ol style={listStyle}>
                        <li><strong>Importação:</strong> Utilize o botão "Importar (.xlsx)" para alimentar a base com os dados mensais da folha/contabilidade.</li>
                        <li><strong>Resumo RTB12:</strong> Acompanhe a margem restante para o Sub Limite e Limite por empresa, com base no faturamento real e projetado.</li>
                        <li><strong>Rateio Orçado e Rateio Notas:</strong> Verifique as alíquotas predefinidas. O sistema calcula a distribuição de receitas, DAS estimado e resultado líquido.</li>
                        <li><strong>Rolling Forecast:</strong> Visualize a evolução do faturamento (realizado vs projetado) dos últimos 12 meses, de forma consolidada ou por empresa, através do gráfico.</li>
                        <li><strong>Controle de Rateio:</strong> (Acesso restrito) Ajuste o percentual de rateio de cada loja ou realize a transferência de lojas entre grupos.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'pl-zscore',
            icon: TrendingUp,
            title: 'Contábil: Análise de P&L (Z-Score)',
            color: '#16a34a',
            desc: 'Identificação estatística de outliers e ineficiências.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como operar a Análise de P&amp;L:</strong></p>
                    <p style={pStyle}>Esta ferramenta avançada é dividida em três recursos integrados:</p>
                    <ol style={listStyle}>
                        <li><strong>Alimentar Banco de Dados:</strong> Faça o upload da planilha mensal de P&amp;L (DRE). O sistema irá formatar os dados, cruzar filiais e contas, e gravar as informações no banco. <em>Aviso: O sistema bloqueia inserções com mais de 3 meses de defasagem para manter a consistência da base.</em></li>
                        <li><strong>Exportar Base (Horizontal):</strong> Permite extrair todo o histórico armazenado no banco num formato "Pivot" (linhas por Filial, colunas por Conta), ideal para montar tabelas dinâmicas no Excel.</li>
                        <li><strong>Gerar Relatório Z-Score:</strong> O núcleo da inteligência. Selecione o período e o idioma (PT ou EN). O algoritmo matemático irá analisar o comportamento de gastos de todas as lojas, identificar desvios padrão (Outliers) e gerar um <strong>Relatório Executivo em PDF</strong> completo, com gráficos de dispersão, ranking e análise qualitativa de custos.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'conferencia-vendas',
            icon: FileSearch,
            title: 'Contábil: Conferência de Vendas',
            color: '#16a34a',
            desc: 'Auditoria entre Balancete e Sistema SOVOS.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como realizar a Conferência:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta de Conferência de Vendas.</li>
                        <li><strong>Processamento:</strong> O sistema fará a leitura e o cruzamento automático entre os valores totais lançados no Balancete e os registros fiscais processados pelo SOVOS.</li>
                        <li><strong>Detalhamento Analítico:</strong> Em caso de divergências de valores, o script rastreia e detalha as chaves de acesso e a classificação das notas para identificar exatamente onde está a diferença.</li>
                        <li><strong>Download:</strong> Você receberá um Excel com uma aba de "Análise" (resumo comparativo por loja) e abas subsequentes com o detalhamento das divergências.</li>
                    </ol>
                </div>
            )
        },
        // ⚠️ Os verbetes 'fechamento-contabil' e 'imobilizado' foram ARQUIVADOS em
        //    2026-09-01 junto com as telas que eles explicavam (ver
        //    backup/contabilidade/). Manual de tela que não existe mais é pior que
        //    manual faltando: manda a pessoa procurar uma aba que sumiu.
    ];

    // ----------------------------------------------------------------------
    // 6. MÓDULO FINANCEIRO (verde-água / teal)
    // ----------------------------------------------------------------------
    const manualFinanceiro = [
        {
            id: 'visao-geral-financeiro',
            icon: PiggyBank,
            title: 'Financeiro: Visão Geral',
            color: '#0d7a74',
            desc: 'Como navegar pelo Módulo do Financeiro.',
            content: (
                <div>
                    <p style={pStyle}>O Módulo Financeiro controla os processos do dia a dia das lojas e as automações de conciliação. As abas:</p>
                    <ul style={listStyle}>
                        <li><strong>Estorno de Venda:</strong> solicitação e acompanhamento de estornos de cartão/PIX.</li>
                        <li><strong>Fita:</strong> confronto entre recebimento e venda, por meio de pagamento.</li>
                        <li><strong>Petty Cash:</strong> fundo fixo de caixa (caixinha).</li>
                        <li><strong>Troco:</strong> o numerário de troco das lojas — envio, depósito, conferência e saldos.</li>
                        <li><strong>Numerário:</strong> confere a venda em dinheiro contra o que a loja declarou e o que a Prosegur depositou.</li>
                        <li><strong>Automações:</strong> Conciliação Adyen, Conferência PIX, Faturas de Cartão Corporativo e <strong>Cálculo dos Impostos Federais</strong>.</li>
                        <li><strong>Nulos em Progresso:</strong> a loja declara o pedido cancelado no POS, com a foto da tela.</li>
                        <li><strong>Relatórios:</strong> Fechamento Mensal na visão financeira.</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'estorno',
            icon: RefreshCcw,
            title: 'Financeiro: Estorno de Venda',
            color: '#0d7a74',
            desc: 'Registro e acompanhamento de pedidos de estorno (cartão e PIX).',
            content: (
                <div>
                    <p style={pStyle}><strong>Aba "Solicitar Estorno":</strong></p>
                    <ol style={listStyle}>
                        <li>Selecione a <strong>loja</strong> e preencha os dados do cliente (nome, telefone, e-mail).</li>
                        <li>Informe a <strong>forma de pagamento</strong>: bandeira do cartão (com número) ou PIX (com a chave).</li>
                        <li>Preencha o <strong>valor da venda</strong>, o <strong>valor a estornar</strong>, a data e a descrição da ocorrência.</li>
                        <li>Anexe o <strong>comprovante</strong> e envie. O sistema guarda o comprovante na pasta <em>EstornoCartao</em> (SharePoint).</li>
                    </ol>
                    <p style={pStyle}><strong>Aba "Controle":</strong> acompanhe o andamento e a situação de cada solicitação.</p>
                </div>
            )
        },
        {
            id: 'fita',
            icon: FileText,
            title: 'Financeiro: Fita',
            color: '#0d7a74',
            desc: 'Confronto entre recebimento e venda, por meio de pagamento.',
            content: (
                <div>
                    <p style={pStyle}>A Fita confronta o que foi <strong>recebido</strong> com o que foi <strong>vendido</strong>, por meio de pagamento. Abas:</p>
                    <ul style={listStyle}>
                        <li><strong>Consulta:</strong> pagamentos por meio de pagamento, filtrados por <strong>período</strong> (de/até) e por uma ou várias <strong>lojas</strong> (botões clicáveis, com atalhos "Todas"/"Limpar"). Após consultar, dá para filtrar os <strong>meios de pagamento</strong> em botões clicáveis — os totais e a exportação acompanham o filtro. Lançamentos cancelados são excluídos automaticamente.</li>
                        <li><strong>Importação:</strong> carga da base da Fita (registra log de importação).</li>
                        <li><strong>Conciliação Resumo de Venda:</strong> bate os totais da fita com o resumo de venda.</li>
                        <li><strong>Débito / Crédito:</strong> apura sobras e faltas entre recebimento e venda, separando por bandeira de débito e crédito.</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'petty-cash',
            icon: Wallet,
            title: 'Financeiro: Petty Cash',
            color: '#0d7a74',
            desc: 'Fundo fixo de caixa (caixinha) para pequenas despesas.',
            content: (
                <div>
                    <p style={pStyle}>Controle do fundo fixo de caixa para pequenas despesas do dia a dia. Abas:</p>
                    <ul style={listStyle}>
                        <li><strong>Registrar Despesa:</strong> lança uma saída do fundo (com comprovante).</li>
                        <li><strong>Controle:</strong> acompanha o saldo e os lançamentos.</li>
                        <li><strong>Relatórios</strong> e <strong>Auditoria:</strong> visões consolidadas, disponíveis para os perfis Financeiro, Admin, Gerente Adm e Auditoria.</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'troco',
            icon: Coins,
            title: 'Financeiro: Troco',
            color: '#0d7a74',
            desc: 'O dinheiro de troco das lojas: envio, depósito, conferência e saldos.',
            content: (
                <div>
                    <p style={pStyle}>Acompanha o numerário de troco de cada loja. As <strong>quatro telas seguem a ordem em que o dinheiro anda</strong>:</p>
                    <ul style={listStyle}>
                        <li><strong>Envio de Troco:</strong> o escritório manda o troco para a loja (só perfil Financeiro/Admin).</li>
                        <li><strong>Controle:</strong> o relatório de tudo que aconteceu — <strong>uma linha por operação</strong>, no formato do relatório de papel.</li>
                        <li><strong>Conferência:</strong> onde o depósito é tratado de ponta a ponta — propor, lançar, confirmar e conferir com o PDV. (Reuniu as quatro telas antigas de depósito.)</li>
                        <li><strong>Saldos:</strong> quanto cada loja tem parado, com extrato em <em>Loja → mês → transações</em>. Gerente e Gerente de Plantão não veem esta tela — a operação deles é a própria loja.</li>
                    </ul>
                    <p style={pStyle}><strong>Depósito com apoio do PDV:</strong> ao lançar o depósito, a tela oferece as <strong>bananas que o caixa registrou</strong> para você escolher em vez de digitar (foi da digitação que nasceram os números errados). O motor <strong>propõe</strong> quais envios cada depósito quita; quando há mais de uma combinação possível, a tela <strong>mostra a ambiguidade</strong> em vez de escolher por você — quem confirma é a pessoa.</p>
                    <p style={pStyle}><strong>Acerto de operação:</strong> nem toda linha é troco. Em <em>Lançar Acerto</em> registram-se a <strong>Devolução</strong> (a loja devolveu numerário ao escritório) e a <strong>Correção</strong> (ajuste de saldo, sem dinheiro se movendo) — sempre com motivo obrigatório.</p>
                    <div style={{ backgroundColor: '#fffbeb', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #f59e0b', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                            <strong>Teto de saldo:</strong> a loja não pode acumular troco parado acima do limite dela. Estourado o teto, o <strong>envio é recusado</strong> até o gerente depositar e o Financeiro confirmar.<br />
                            <strong>O PDV é apoio, nunca trava:</strong> o dado do caixa chega com um dia de atraso, então depósito recente aparece como <em>"aguardando PDV"</em> — e isso pode ser confirmado normalmente.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'numerario',
            icon: Scale,
            title: 'Financeiro: Numerário',
            color: '#0d7a74',
            desc: 'Confere a venda em dinheiro contra o declarado e o depositado.',
            content: (
                <div>
                    <p style={pStyle}>Confere o dinheiro em <strong>três pernas</strong>: o <strong>Realizado</strong> (a venda em dinheiro pela nota fiscal), o <strong>Declarado</strong> (o que a loja registrou no caixa) e o <strong>Apurado</strong> (o que a Prosegur informa ter depositado no banco).</p>
                    <p style={pStyle}><strong>As abas seguem o trabalho da semana:</strong></p>
                    <ol style={listStyle}>
                        <li><strong>Importar relatório:</strong> carrega o PDF semanal da Prosegur (guias e envelopes).</li>
                        <li><strong>Por dia:</strong> a leitura diária, loja a loja.</li>
                        <li><strong>Conferência:</strong> Realizado × Declarado por <strong>semana (quarta a terça)</strong> e o depósito conferido <strong>por guia</strong>.</li>
                        <li><strong>Lançamento SAP:</strong> monta o lançamento contábil pronto para transcrever.</li>
                    </ol>
                    <p style={pStyle}><strong>Duas comparações que nunca se misturam:</strong> a venda se compara por semana; o depósito, por guia — porque a guia não respeita o calendário (há envelope que chega semanas depois). Quando falta dia de caixa no período, o resultado é <strong>"Declarado incompleto"</strong>, não "falta dinheiro".</p>
                    <div style={{ backgroundColor: '#fffbeb', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #f59e0b', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                            <strong>O Lançamento SAP não integra com o SAP</strong> — ele monta o lançamento para você transcrever.<br />
                            <strong>O período padrão é de 1 semana</strong>, mas quando os envelopes do malote indicam mais, a tela sinaliza (<em>"os envelopes indicam N — usar"</em>). Ignorar esse aviso faz aparecer uma "diferença" de milhares de reais que é, na verdade, uma semana não creditada.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'conciliacao-adyen',
            icon: FileCheck2,
            title: 'Financeiro: Conciliação Adyen',
            color: '#0d7a74',
            desc: 'Automatiza a leitura e conciliação financeira de relatórios Adyen.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como conciliar relatórios da Adyen:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta Conciliação Adyen.</li>
                        <li>Faça o upload múltiplo dos relatórios de <strong>Detalhes de Liquidação (.xlsx)</strong> e <strong>Recebimentos (.csv)</strong>.</li>
                        <li>Clique em <strong>"Iniciar Conciliação"</strong>. O robô fará a limpeza e agrupará as transações por loja automaticamente.</li>
                        <li>Um relatório consolidado com formatação contábil em Excel será baixado ao final do processo.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'conferencia-pix',
            icon: QrCode,
            title: 'Financeiro: Conferência PIX',
            color: '#0d7a74',
            desc: 'Cruza relatórios PIX ATOS com extratos bancários.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como realizar a Conferência PIX:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta Conferência PIX.</li>
                        <li><strong>Upload dos Ficheiros:</strong> Carregue os relatórios de vendas PIX extraídos do sistema de caixa (ATOS) e os respetivos ficheiros de extrato bancário.</li>
                        <li><strong>Processamento:</strong> A automação irá cruzar as duas fontes, comparando os valores transacionados na operação com os valores efetivamente depositados em conta.</li>
                        <li><strong>Relatório de Consolidação:</strong> Download de um Excel consolidado, agrupando os totais por empresa e evidenciando qualquer quebra ou divergência financeira.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'fatura-cartao',
            icon: CreditCard,
            title: 'Financeiro: Faturas Cartão Corporativo',
            color: '#0d7a74',
            desc: 'Extração automática de faturas em PDF para Excel.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como extrair e categorizar Faturas:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta de Faturas Cartão Corporativo.</li>
                        <li><strong>Upload:</strong> Carregue o ficheiro PDF original da fatura do cartão de crédito corporativo.</li>
                        <li><strong>Processamento:</strong> O sistema lê o PDF e identifica as transações de cada colaborador (Data, Descrição, Moeda de Origem, Valores em US$ / R$ e a Cotação do Dólar aplicada).</li>
                        <li><strong>Download:</strong> Um Excel onde cada colaborador tem o seu próprio separador (aba) com as despesas organizadas, facilitando a conferência e a alocação de custos.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'calculo-impostos-federais',
            icon: Landmark,
            title: 'Financeiro: Cálculo dos Impostos Federais',
            color: '#0d7a74',
            desc: 'Transforma a planilha de folha no cálculo do mês, com os lançamentos para o SAP.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como gerar o cálculo do mês:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta Cálculo dos Impostos Federais.</li>
                        <li><strong>Envie dois arquivos:</strong> a <strong>planilha de folha</strong> do mês — a única que o RH manda, com as abas INSS, IRRF e FGTS — e o <strong>CALCULO IMPOSTOS FEDERAIS do mês passado</strong>. ⚠️ O segundo <strong>não é uma planilha de PIS/COFINS</strong>: é o próprio arquivo que você já tem do mês anterior, entrando como <strong>modelo</strong>. Ele é necessário porque as abas de PIS/COFINS vêm da apuração fiscal e não da folha — então são aproveitadas de lá, e todo o resto é refeito com os dados do mês novo.</li>
                        <li>Clique em <strong>"Gerar Cálculo"</strong>. O arquivo é baixado pronto, com o resumo de cada empresa, os blocos de lançamento para o SAP loja por loja e o fechamento do FGTS.</li>
                        <li><strong>Antes de lançar, abra a aba CONFERENCIA.</strong> É lá que fica o que o sistema apurou mas não decide sozinho.</li>
                    </ol>

                    <p style={pStyle}><strong>O que a aba CONFERENCIA mostra:</strong></p>
                    <ul style={listStyle}>
                        <li><strong>A ALOCAR</strong> — a diferença entre o que foi apurado e o DCTFWEB declarado na folha, empresa por empresa, destacada em amarelo. O sistema <strong>não</strong> distribui essa diferença: em qual filial lançá-la é decisão de quem concilia.</li>
                        <li><strong>Avisos</strong> — o que precisa de conferência antes do lançamento.</li>
                    </ul>

                    <p style={pStyle}><strong>Dois avisos que valem atenção:</strong></p>
                    <ul style={listStyle}>
                        <li><strong>"PIS/COFINS ausente na folha"</strong> — as colunas vieram zeradas. ⚠️ A planilha <strong>fecha certinho assim mesmo</strong>, porque o total declarado vem menor junto: aconteceu em maio/2026 e o mês bateu ao centavo com o imposto faltando. Confira se foi esquecimento de quem preencheu.</li>
                        <li><strong>"PIS/COFINS lido do template"</strong> — significa que os valores são os do mês anterior, não os do mês que está sendo gerado. Atualize as abas de PIS/COFINS com a apuração do mês certo.</li>
                    </ul>

                    <p style={pStyle}><strong>A competência é lida de dentro da planilha</strong>, no título da aba INSS — nunca pelo nome do arquivo, que muda de um mês para o outro.</p>
                </div>
            )
        },
        {
            id: 'fechamento-financeiro',
            icon: BarChart3,
            title: 'Financeiro: Fechamento Mensal',
            color: '#0d7a74',
            desc: 'Faturamento consolidado do mês, visão financeira.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como extrair o Fechamento (visão Financeira):</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Relatórios</strong>, abra o cartão "Fechamento Mensal (Financeiro)".</li>
                        <li><strong>Parâmetros:</strong> Selecione o período. A visão já é a financeira.</li>
                        <li><strong>Processamento:</strong> Consulta direta ao banco — sem upload de planilhas.</li>
                        <li><strong>Resultado:</strong> Relatório consolidado para visualizar na tela e exportar em Excel.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'nulos',
            icon: FileWarning,
            title: 'Financeiro: Nulos em Progresso',
            color: '#0d7a74',
            desc: 'Declarar o pedido cancelado no POS e acompanhar os registros.',
            content: (
                <div>
                    <p style={pStyle}>
                        <strong>Nulo em progresso</strong> é o pedido cancelado no POS antes de a venda fechar.
                        A loja declara cada um na aba <strong>Registrar Nulo</strong>, com a <strong>foto da tela do POS</strong>.
                    </p>
                    <ul style={listStyle}>
                        <li><strong>Quem registra:</strong> o gerente e o gerente de plantão, sempre na própria loja (a loja aparece preenchida, sem escolha). Admin e Diretoria escolhem a loja.</li>
                        <li><strong>Gerente responsável:</strong> escolhido numa lista com os gerentes ativos daquela loja — não é campo digitado.</li>
                        <li><strong>Foto obrigatória:</strong> sem a evidência não há registro. No celular, o botão abre a câmera direto.</li>
                        <li><strong>"Foi estornado para o cliente?"</strong> e <strong>"Cobrado novamente o valor do cliente?"</strong> se leem <strong>em par</strong>: estornado <em>Sim</em> com recobrança <em>Não</em> é o valor que voltou ao cliente e nunca foi recobrado — é esse par que o placar <strong>Estornado sem recobrança</strong> conta. Por isso nenhuma das duas vem marcada.</li>
                        <li><strong>O registro é definitivo.</strong> A loja não edita nem apaga: correção é pedida ao Financeiro ou à Auditoria, que <strong>cancela com motivo</strong>. A linha cancelada continua visível, fora dos totais.</li>
                    </ul>
                    <p style={pStyle}>
                        Na aba <strong>Controle de Nulos</strong>, filtre por período, loja, forma de pagamento e "estornado",
                        veja o placar do período (quantidade, valor e quanto foi estornado), abra a evidência de cada linha
                        e exporte a lista em <strong>CSV</strong> — o arquivo sai exatamente com o que está na tela.
                    </p>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // 7. MÓDULO RH (vermelho)
    // ----------------------------------------------------------------------
    const manualRh = [
        {
            id: 'visao-geral-rh',
            icon: Users,
            title: 'RH: Visão Geral',
            color: '#e11d48',
            desc: 'Como navegar pelo Módulo de Recursos Humanos.',
            content: (
                <div>
                    <p style={pStyle}>O Módulo de RH reúne os processos centrais do departamento:</p>
                    <ul style={listStyle}>
                        <li><strong>Atualização de Cadastro:</strong> o gerente informa troca de e-mail/telefone do funcionário e o RH corrige no sistema.</li>
                        <li><strong>Automações:</strong> benefícios e folha — DIRF × eSocial, Benefício Social, Empréstimo Consignado, Vale Transporte, iFood, VR e Wellhub.</li>
                        <li><strong>Controle de Experiência:</strong> ex-funcionários recontratados que não devem assinar novo contrato de experiência.</li>
                        <li><strong>Transição de Cargo:</strong> promoções de Coordenador Q&amp;S e Gerente de Plantão.</li>
                        <li><strong>Formulários:</strong> Entrevista de Desligamento, Primeiros 15 Dias e Pesquisa de Experiência.</li>
                        <li><strong>Destaque do Mês:</strong> eleição dos funcionários exemplares de cada loja.</li>
                        <li><strong>Término do Contrato de Experiência:</strong> recém-contratados que faltaram na primeira semana.</li>
                    </ul>
                    <p style={pStyle}>O <strong>PPI (Promoção Interna)</strong> saiu do RH e hoje vive no módulo <strong>Treinamento &amp; Qualidade</strong> — o RH acessa por lá, e enxerga só as abas dele.</p>
                </div>
            )
        },
        {
            id: 'rh-dirf-esocial',
            icon: ShieldCheck,
            title: 'RH: Análise DIRF vs eSocial (Plano de Saúde)',
            color: '#e11d48',
            desc: 'Cruzamento das bases Total, Titulares e Dependentes.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como processar a Análise de Saúde:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta DIRF vs eSocial <em>(acesso exclusivo para Administradores)</em>.</li>
                        <li><strong>Upload das Bases:</strong> Insira simultaneamente os três relatórios extraídos: Base Total, Base de Titulares e Base de Dependentes.</li>
                        <li><strong>Mapeamento Automático:</strong> O algoritmo cruza os CPFs, identifica titular e dependente, e vincula o código ANS ao CNPJ e Nome da Operadora (Unimed, Samp, Odontoprev, etc.).</li>
                        <li><strong>Resultado:</strong> Excel com duas abas — um "Resumo" consolidado e um "Detalhamento" que expõe as divergências de valores para correção antes das entregas fiscais.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'beneficio-social',
            icon: FileSpreadsheet,
            title: 'RH: Benefício Social',
            color: '#e11d48',
            desc: 'Extração de dados de funcionários ativos em Excel legado (.xls).',
            content: (
                <div>
                    <p style={pStyle}><strong>Como extrair os benefícios:</strong></p>
                    <ul style={listStyle}>
                        <li>Esta automação conecta-se diretamente ao banco (tabela dtbConfere) <strong>em tempo real</strong>, dispensando planilhas locais.</li>
                        <li>Verifique no painel a "Última Atualização no Banco" para confirmar se os dados do sistema principal já foram sincronizados hoje.</li>
                        <li>Clique em <strong>"Iniciar Extração"</strong>. O sistema gera planilhas separadas por Centro de Custo no formato legado 97-2003 (.xls) e as disponibiliza num único arquivo compactado (.zip).</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'emprestimo',
            icon: Landmark,
            title: 'RH: Empréstimo Consignado',
            color: '#e11d48',
            desc: 'Conferência da folha referente a empréstimos consignados.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como processar a conferência:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta de Empréstimo Consignado.</li>
                        <li>Na primeira zona de upload (esquerda), anexe a <strong>Planilha da Folha</strong> no formato Excel (.xlsx).</li>
                        <li>Na segunda zona (direita), selecione os múltiplos <strong>arquivos do sistema bancário</strong> no formato (.csv).</li>
                        <li>Inicie o processamento. O sistema faz o cruzamento e exporta a planilha de inconsistências apontando as divergências.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'rh-auditoria-vt',
            icon: Users,
            title: 'RH: Vale Transporte (GvBus)',
            color: '#e11d48',
            desc: 'Auditoria e conciliação de créditos de VT (GvBus vs Populis).',
            content: (
                <div>
                    <p style={pStyle}><strong>Como auditar e conciliar o Vale Transporte:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta de Vale Transporte.</li>
                        <li><strong>Upload de Ficheiros:</strong> Carregue os relatórios de funcionários do sistema de RH (Populis) e os relatórios de saldos/extratos da concessionária (GvBus).</li>
                        <li><strong>Auditoria Preventiva:</strong> O sistema verifica se há colaboradores recém-admitidos e ativos que ficaram sem o benefício cadastrado, prevenindo falhas no pagamento.</li>
                        <li><strong>Fechamento e Importação:</strong> A automação cruza os valores e gera o download de um arquivo comprimido (<code>.zip</code>) com a planilha Excel de conferência e um ficheiro <code>.txt</code> formatado e pronto para importação na folha.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'ifood-beneficios',
            icon: PiggyBank,
            title: 'RH: iFood Benefícios',
            color: '#e11d48',
            desc: 'Separa o relatório em arquivos individuais por Centro de Custo.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como processar o relatório:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta iFood Benefícios.</li>
                        <li>Na zona de upload, anexe a <strong>planilha base</strong> com as informações dos colaboradores (Excel .xlsx/.xls ou CSV).</li>
                        <li>Clique em <strong>"Iniciar Processamento"</strong>. O sistema faz a limpeza e separa os dados por Centro de Custo.</li>
                        <li>Ao final, o relatório formatado (<code>Relatorio_iFood_Beneficios.xlsx</code>) é baixado automaticamente.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'vr',
            icon: CreditCard,
            title: 'RH: VR (Vale Refeição)',
            color: '#e11d48',
            desc: 'Crédito do VR cruzando a operadora com o Populis.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como gerar o crédito do VR:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta VR.</li>
                        <li>Faça o upload do relatório da operadora <strong>VR</strong> e do relatório do <strong>Populis</strong> (sistema de RH).</li>
                        <li>O sistema cruza as duas bases para conferir e gerar o crédito do Vale Refeição.</li>
                        <li>Baixe o resultado da conferência ao final do processamento.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'rh-wellhub',
            icon: Heart,
            title: 'RH: Wellhub',
            color: '#e11d48',
            desc: 'Gera as planilhas de Adição e Remoção de colaboradores.',
            content: (
                <div>
                    <p style={pStyle}><strong>Como gerar as planilhas do Wellhub:</strong></p>
                    <ol style={listStyle}>
                        <li>Na aba <strong>Automações</strong>, abra a ferramenta Wellhub.</li>
                        <li>Envie a <strong>base ativa do Wellhub</strong>.</li>
                        <li>O sistema cruza essa base com o sistema interno e gera as planilhas de <strong>Adição</strong> e <strong>Remoção</strong> de colaboradores.</li>
                        <li>Baixe as planilhas geradas.</li>
                    </ol>
                </div>
            )
        },
        {
            id: 'controle-experiencia',
            icon: UserCheck,
            title: 'RH: Controle de Experiência',
            color: '#e11d48',
            desc: 'Recontratados que não devem assinar novo contrato de experiência.',
            content: (
                <div>
                    <p style={pStyle}>Identifica <strong>ex-funcionários recontratados no mesmo cargo e empresa</strong> que, por lei, não devem assinar um novo contrato de experiência. Abas:</p>
                    <ul style={listStyle}>
                        <li><strong>Pendentes:</strong> lista os casos com os <strong>dias restantes</strong> até o vencimento (verde &gt; 15 dias, amarelo ≤ 15, vermelho vencido) e o <strong>status do Populis</strong>. O selo <em>"Populis OK"</em> aparece quando a dtbConfere já sincronizou (DATA_FINAL_EXPERIENCIA = DATA_ADMISSAO) — o sync roda às 04:30, 12:30 e 16:20. Registre a verificação com uma observação.</li>
                        <li><strong>Histórico:</strong> consulta dos casos já tratados.</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'transicao-cargo',
            icon: TrendingUp,
            title: 'RH: Transição de Cargo',
            color: '#e11d48',
            desc: 'Promoções de Coordenador Q&S e Gerente de Plantão.',
            content: (
                <div>
                    <p style={pStyle}>Acompanha as promoções de <strong>Coordenador Q&amp;S</strong> (JR → PL → SR) e <strong>Gerente de Plantão</strong> (JR → PL I/II/III → SR I/II/III). O topo da escada (Coord SR / GP SR III) segue para o PPI.</p>
                    <p style={pStyle}><strong>Dois regimes de elegibilidade:</strong></p>
                    <ul style={listStyle}>
                        <li><strong>Junior:</strong> ≥ 6 meses no cargo; se reprovar, repete em +6 meses.</li>
                        <li><strong>Anual (PL/SR):</strong> 1 ano no cargo até 31/03, com janela de avaliação em Jan–Fev.</li>
                    </ul>
                    <p style={pStyle}><strong>Abas:</strong></p>
                    <ul style={listStyle}>
                        <li><strong>Elegíveis:</strong> monitoramento calculado ao vivo (tempo de cargo + histórico).</li>
                        <li><strong>Lançar Avaliação:</strong> o RH lança 3 notas (0–100): <em>LIVE/prática</em>, <em>Teórica</em> e <em>Desempenho</em>. A nota final = LIVE×0,35 + Teórica×0,35 + Desempenho×0,30; <strong>≥ 90 = Aprovado</strong> (a prévia aparece na tela).</li>
                        <li><strong>Histórico:</strong> avaliações lançadas e a efetivação.</li>
                    </ul>
                    <p style={pStyle}>O portal <strong>não escreve o cargo</strong>: ele detecta a efetivação quando o cargo sobe na escada no sistema de RH.</p>
                </div>
            )
        },
        {
            id: 'rh-entrevista-desligamento',
            icon: LogOut,
            title: 'RH: Entrevista de Desligamento',
            color: '#e11d48',
            desc: 'Mapeamento de motivos de saída e turnover.',
            content: (
                <div>
                    <p style={pStyle}>Na aba <strong>Formulários → Entrevista de Desligamento</strong>, aplicada ao finalizar o contrato de um colaborador.</p>
                    <ul style={listStyle}>
                        <li><strong>Preencher:</strong> registre a data de demissão, conduza o questionário sobre empresa, liderança e ambiente de trabalho, e o motivo principal da saída.</li>
                        <li><strong>Relatórios:</strong> o painel consolida os <strong>Principais Motivos de Saída</strong>, ajudando o RH a identificar padrões de turnover e pontos de melhoria.</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'rh-primeiro15',
            icon: Clock,
            title: 'RH: Primeiros 15 Dias',
            color: '#e11d48',
            desc: 'Avaliação de integração e adaptação do novo colaborador.',
            content: (
                <div>
                    <p style={pStyle}>Na aba <strong>Formulários → Primeiros 15 Dias</strong>.</p>
                    <ul style={listStyle}>
                        <li><strong>Consultar:</strong> lista os colaboradores no período de experiência que precisam ser avaliados.</li>
                        <li><strong>Preencher:</strong> pontue quesitos como Pontualidade, Aprendizado e Relacionamento Interpessoal e defina o parecer final — <strong>Aprovado, Reprovado ou Prorrogado</strong>.</li>
                        <li><strong>Relatórios:</strong> taxa de retenção dos novos talentos pelos cartões de Aprovados e Reprovados.</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'rh-pesquisa-experiencia',
            icon: Activity,
            title: 'RH: Pesquisa de Experiência do Colaborador',
            color: '#e11d48',
            desc: 'Fatores psicossociais do trabalho (apoio ao PGR).',
            content: (
                <div>
                    <p style={pStyle}>Na aba <strong>Formulários → Pesquisa de Experiência do Colaborador</strong>. Mapeia fatores psicossociais do trabalho para orientar ações preventivas e registrar evidências na gestão de riscos do <strong>PGR</strong>.</p>
                    <ul style={listStyle}>
                        <li><strong>Consultar:</strong> acompanha as respostas recebidas.</li>
                        <li><strong>Relatórios:</strong> consolida os resultados para leitura por loja e pela rede.</li>
                    </ul>
                </div>
            )
        },
        {
            id: 'rh-destaque-mes',
            icon: Award,
            title: 'RH: Destaque do Mês',
            color: '#e11d48',
            desc: 'Eleição dos funcionários exemplares de cada loja, com foto para o Marketing.',
            content: (
                <div>
                    <p style={pStyle}>Aponta, por loja, os funcionários <strong>exemplares do mês anterior</strong> e deixa o gerente <strong>eleger</strong> os destaques. A lista de elegíveis é calculada ao vivo do <strong>espelho de ponto</strong> — não é digitada por ninguém.</p>
                    <p style={pStyle}><strong>Quem é elegível:</strong> sem falta, sem atestado e sem atraso no mês, ativo, fora da gestão e com mais de 60 dias de casa.</p>
                    <p style={pStyle}><strong>Como funciona:</strong></p>
                    <ol style={listStyle}>
                        <li>A tela mostra os <strong>elegíveis</strong> do mês fechado e uma <strong>prévia do mês em andamento</strong>.</li>
                        <li>O gerente elege <strong>2 por loja</strong> — nas lojas 24h (ANV, VEL e SED) são <strong>4</strong>.</li>
                        <li>Depois de eleger, a tela passa a mostrar só os <strong>cards dos destaques</strong>, onde se anexa a <strong>foto</strong> de cada um (é dela que o Marketing tira a divulgação) e, se quiser, o <strong>comprovante</strong> da premiação (imagem ou PDF).</li>
                    </ol>
                    <div style={{ backgroundColor: '#fffbeb', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #f59e0b', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                            <strong>Dois prazos governam a tela.</strong> A eleição só é aceita <strong>do dia 1º às 12h até o dia 3 às 12h</strong> — para todo perfil, inclusive Admin, RH e Diretoria — e <strong>é definitiva</strong> (não dá para trocar depois). Fora da janela, consultar os elegíveis e ver os eleitos continua liberado o mês inteiro. O módulo apura de <strong>julho/2026</strong> em diante.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'rh-cancelamento-contrato',
            icon: CalendarX2,
            title: 'RH: Término do Contrato de Experiência',
            color: '#e11d48',
            desc: 'Recém-contratados que faltaram na primeira semana.',
            content: (
                <div>
                    <p style={pStyle}>Lista <strong>ao vivo</strong> os recém-contratados que faltaram na <strong>primeira semana</strong>, cruzando a data de admissão com as faltas do espelho de ponto. O gerente pede o cancelamento; o RH decide.</p>
                    <p style={pStyle}><strong>Abas:</strong></p>
                    <ul style={listStyle}>
                        <li><strong>Candidatos:</strong> o <strong>gerente</strong> vê os casos da própria loja e abre o pedido. O RH e a Diretoria veem a rede inteira, em leitura, para acompanhar antes do pedido chegar.</li>
                        <li><strong>Solicitações:</strong> o RH e a Diretoria <strong>aprovam ou reprovam</strong>; gerente e Gerente de Plantão acompanham a fila da própria loja.</li>
                        <li><strong>Destinatários:</strong> quem recebe o e-mail de aviso.</li>
                    </ul>
                    <p style={pStyle}><strong>Como abrir o pedido:</strong> a tela mostra os dias faltantes e pergunta <em>"O funcionário teve presença nesses dias?"</em>. Respondendo <strong>Sim</strong>, o caso é dispensado; <strong>Não</strong> abre a solicitação, com <strong>justificativa obrigatória</strong>.</p>
                    <p style={pStyle}>O próprio sistema <strong>classifica</strong> o pedido: faltou já no dia da admissão → <em>cancelamento de admissão</em>; só faltou depois → <em>cancelamento de contrato de experiência</em>.</p>
                    <div style={{ backgroundColor: '#fef2f2', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #e11d48', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#991b1b', margin: 0 }}>
                            <strong>O portal não desliga ninguém.</strong> Ele é solicitação, notificação e rastro da decisão — o desligamento continua sendo feito no sistema de RH.
                        </p>
                    </div>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // 8. (vago) — era o MÓDULO AUDITORIA (laranja). O único verbete
    //    (`checklist-auditoria`) mudou para `manualOperacional` em 2026-09-23
    //    (DEM-00011, Fase 4, T21), junto com a tela. O hub Auditoria fica sem
    //    Manual da Tela até a Fase 5 encerrá-lo (não há mais card para explicar).
    // ----------------------------------------------------------------------

    // ----------------------------------------------------------------------
    // 9. MÓDULO MAPA DE CAIXA (azul)
    // ----------------------------------------------------------------------
    const manualMapaCaixa = [
        {
            id: 'mapa-upload',
            icon: UploadCloud,
            title: 'Mapa de Caixa: Enviar Mapa',
            color: '#0ea5e9',
            desc: 'Upload do PDF para extração via IA.',
            content: (
                <div>
                    <p style={pStyle}>Na aba <strong>Enviar Mapa de Caixa</strong>, faça o upload do <strong>PDF</strong> do mapa do dia. O sistema lê o arquivo e <strong>extrai os valores via IA</strong>, dispensando a digitação manual.</p>
                </div>
            )
        },
        {
            id: 'mapa-controle',
            icon: CheckCircle,
            title: 'Mapa de Caixa: Conferência',
            color: '#10b981',
            desc: 'Validação dos valores extraídos e acesso ao SharePoint.',
            content: (
                <div>
                    <p style={pStyle}>Na aba <strong>Conferência</strong>, valide os valores que a IA extraiu do mapa antes de consolidar. A partir daqui você também acessa os arquivos no <strong>SharePoint</strong>.</p>
                </div>
            )
        },
        {
            id: 'mapa-relatorios',
            icon: BarChart3,
            title: 'Mapa de Caixa: Relatórios Financeiros',
            color: '#8b5cf6',
            desc: 'Consolidado de pagamentos, PIX e cartões.',
            content: (
                <div>
                    <p style={pStyle}>Na aba <strong>Relatórios Financeiros</strong>, veja o consolidado de pagamentos, <strong>PIX</strong> e <strong>cartões</strong> a partir dos mapas de caixa enviados.</p>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // 10. MÓDULO MANUTENÇÃO (índigo)
    // ----------------------------------------------------------------------
    const manualManutencao = [
        {
            id: 'visao-geral-manutencao',
            icon: HardHat,
            title: 'Manutenção: Visão Geral',
            color: '#4f46e5',
            desc: 'Como navegar pelo Módulo de Manutenção.',
            content: (
                <div>
                    <p style={pStyle}>O Módulo de Manutenção tem <strong>dois programas</strong>, e a diferença entre eles é simples: um cuida do <strong>equipamento</strong>, o outro cuida do <strong>prédio</strong>.</p>
                    <ul style={listStyle}>
                        <li><strong>Programa de Certificação de Equipamentos:</strong> o relatório anual de cada equipamento (fritadeira, chapa…), em duas etapas — diagnóstico e certificação.</li>
                        <li><strong>Programa Anual de Conservação Predial:</strong> a inspeção do prédio da unidade, com nota (ICP) e Ordens de Serviço.</li>
                    </ul>
                    <p style={pStyle}>Os dois seguem a mesma lógica: alguém <strong>monta o modelo</strong> (versão publicada), alguém <strong>preenche em campo</strong>, e no fim <strong>gerente e consultor validam</strong> para o documento virar definitivo.</p>
                    <p style={pStyle}>O <strong>gerente da loja</strong> entra só para consultar e validar o que é da própria unidade.</p>
                </div>
            )
        },
        {
            id: 'certificacao-equipamentos',
            icon: ClipboardCheck,
            title: 'Manutenção: Certificação de Equipamentos',
            color: '#4f46e5',
            desc: 'Diagnóstico (Etapa 1), certificação (Etapa 2) e o certificado em PDF.',
            content: (
                <div>
                    <p style={pStyle}>A tela inicial é a de <strong>Relatórios gravados</strong> (a tabela por loja). Os botões de ação ficam no cabeçalho dela.</p>
                    <p style={pStyle}><strong>O caminho completo, na ordem:</strong></p>
                    <ol style={listStyle}>
                        <li><strong>Monte o modelo</strong> — em <em>Gerenciar versões → Nova versão</em>: escolha o tipo de equipamento, adicione os <strong>sistemas</strong> com o <strong>peso</strong> de cada um e os <strong>componentes</strong> de cada sistema. Marque como <strong>bloqueante</strong> o componente que, se estiver Crítico, reprova a certificação sozinho. Publique.</li>
                        <li><strong>Cadastre os equipamentos físicos</strong> — em <em>Equipamentos → Novo equipamento</em>: patrimônio (único), tipo, loja, fabricante, modelo, série e ano. Cada máquina real é um cadastro; os relatórios ao longo dos anos apontam para ela.</li>
                        <li><strong>Etapa 1 — Diagnóstico:</strong> em <em>Novo Diagnóstico</em>, escolha o formulário publicado, selecione o equipamento e avalie cada componente como <strong>Conforme / Desgaste / Crítico</strong>. As <strong>6 fotos ANTES</strong> são obrigatórias. O <strong>Índice de Saúde</strong> aparece ao vivo. Ao salvar, o relatório fica "Em diagnóstico".</li>
                        <li><strong>Etapa 2 — Certificação:</strong> depois do reparo, abra o relatório e clique em <strong>Certificar</strong>: registre as ações, as peças (R$), a limpeza, os testes, as medições, as pendências, as <strong>fotos DEPOIS</strong> (obrigatórias), o parecer e o aceite. Ao <strong>Concluir</strong>, sai o <strong>certificado em PDF</strong>.</li>
                    </ol>
                    <p style={pStyle}><strong>Validação em duas assinaturas:</strong> cada etapa é validada pelo <strong>gerente</strong> e pelo <strong>consultor</strong>, cada um com sua observação — que <strong>trava ao salvar</strong>. O diagnóstico é um <strong>portão</strong>: a Etapa 2 só começa depois das duas validações, e a certificação só vira definitiva quando os dois validam de novo.</p>
                    <div style={{ backgroundColor: '#eef2ff', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #4f46e5', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#3730a3', margin: 0 }}>
                            <strong>Como o resultado é decidido</strong><br />
                            Pontos: Conforme = 2 · Desgaste = 1 · Crítico = 0. Índice de Saúde = pontos obtidos ÷ máximos, ponderado pelo peso do sistema.<br />
                            Classificação: ≥95% Excelente · 90–94% Muito Bom · 80–89% Bom · 70–79% Atenção · &lt;70% Crítico.<br />
                            Certificação: ≥85% <strong>Certificado</strong> · 70–84% <strong>Com Restrições</strong> · &lt;70% <strong>Não Certificado</strong>.<br />
                            Um item <strong>bloqueante</strong> em Crítico, falha em teste ou risco apontado pelo especialista <strong>reprova mesmo com percentual alto</strong>.
                        </p>
                    </div>
                    <p style={pStyle}>As peças levantadas no diagnóstico viram automaticamente uma <strong>solicitação no módulo Compras</strong> — ninguém redigita a lista.</p>
                </div>
            )
        },
        {
            id: 'conservacao-predial',
            icon: Building2,
            title: 'Manutenção: Conservação Predial',
            color: '#4f46e5',
            desc: 'Inspeção anual do prédio, ICP, Ordens de Serviço e certificação.',
            content: (
                <div>
                    <p style={pStyle}>A tela inicial é <strong>Programas gravados</strong> (Loja › Ano › Programa), com os botões de ação no cabeçalho.</p>
                    <ol style={listStyle}>
                        <li><strong>Monte o checklist</strong> — em <em>Gerenciar versões → Nova versão</em>: crie as <strong>áreas na ordem do roteiro físico</strong> da inspeção (fachada → salão → cozinha → áreas técnicas) e os itens de cada uma. Marque "aceita N/A" nos itens que podem não existir na unidade (playground, cancela, guarita). Publique.</li>
                        <li><strong>Faça a inspeção</strong> — classifique cada item como <strong>Conforme</strong>, <strong>Desgaste</strong> ou <strong>Necessita Intervenção</strong>. Nos dois últimos, o sistema exige <strong>descrição, foto, especialidade e prioridade</strong> — é exatamente o que a Ordem de Serviço precisa. O <strong>ICP</strong> aparece ao vivo.</li>
                        <li><strong>Registre o que está fora da lista</strong> — ao fim de cada área, use <em>+ Adicionar não conformidade</em>. Esses achados também geram Ordem de Serviço.</li>
                        <li><strong>Conclua</strong> — o sistema calcula o ICP e <strong>gera as Ordens de Serviço</strong> automaticamente.</li>
                        <li><strong>Toque as Ordens de Serviço</strong> — andamento <em>Pendente → Em andamento → Concluído</em>. Concluir exige o <strong>serviço descrito e a foto do DEPOIS</strong>.</li>
                        <li><strong>Certifique</strong> — gerente e consultor validam e o programa vira <strong>Certificado</strong>, com o Relatório Técnico em PDF.</li>
                    </ol>
                    <div style={{ backgroundColor: '#eef2ff', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #4f46e5', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#3730a3', margin: 0 }}>
                            <strong>Como o ICP é calculado</strong><br />
                            Conforme = 100% · Desgaste = 50% · Necessita Intervenção = 0%. Item marcado <strong>N/A fica fora da conta</strong> — a loja não é penalizada por não ter algo que nunca existiu ali.<br />
                            Classificação: ≥95% Excelente · 90–94% Muito Bom · 80–89% Bom · 70–79% Regular · &lt;70% Necessita Intervenção.<br />
                            <strong>As não conformidades fora do checklist não entram no ICP</strong> — se entrassem, inspecionar com rigor derrubaria a nota da loja. Elas contam nos indicadores e geram Ordem de Serviço.
                        </p>
                    </div>
                    <p style={pStyle}><strong>Duas travas que costumam gerar dúvida:</strong> depois de finalizada, a inspeção <strong>não é mais editável</strong> (finalizar é porta de sentido único, porque as OS já nasceram dali); e <strong>não se certifica com OS em aberto</strong> — seria atestar um serviço que não foi feito.</p>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // 11. MÓDULO OPERACIONAL (ardósia)
    // ----------------------------------------------------------------------
    const manualOperacional = [
        {
            id: 'visao-geral-operacional',
            icon: Boxes,
            title: 'Operacional: Visão Geral',
            color: '#475569',
            desc: 'Como navegar pelo Módulo Operacional.',
            content: (
                <div>
                    <p style={pStyle}>O Módulo Operacional reúne as ferramentas da operação. Hoje tem dois sub-módulos: a <strong>Avaliação BSV</strong> — a visita de padrões de loja, preenchida pelo escritório — e a <strong>Autoavaliação de Controles</strong>, que o <strong>gerente</strong> responde sobre a própria loja, uma vez por mês.</p>
                    <p style={pStyle}>Conforme novas telas operacionais chegarem, elas aparecem como cards neste mesmo hub.</p>
                </div>
            )
        },
        {
            id: 'bsv',
            icon: ClipboardCheck,
            title: 'Operacional: Avaliação BSV',
            color: '#475569',
            desc: 'Visita de padrões de loja — checklist de conformidade por área.',
            content: (
                <div>
                    <p style={pStyle}>A tela inicial é a de <strong>Avaliações gravadas</strong> (agrupadas por loja); os botões de ação ficam no cabeçalho dela.</p>
                    <ol style={listStyle}>
                        <li><strong>Monte o formulário</strong> — em <em>Gerenciar versões → Nova versão</em>: nomeie a versão, crie as <strong>áreas</strong> da loja e, dentro de cada uma, as <strong>perguntas</strong> com o <strong>peso</strong>. Publique.</li>
                        <li><strong>Faça a avaliação</strong> — em <em>Nova Avaliação</em>, escolha o formulário publicado, informe loja, data, avaliador e os gerentes, e responda cada pergunta como <strong>Sim / Não / N/A</strong>. Marcar <strong>"Não" exige ao menos uma foto</strong> da evidência (a observação é opcional). A conformidade aparece ao vivo, por área e no total.</li>
                        <li><strong>Saia e volte quando quiser</strong> — o preenchimento é <strong>salvo automaticamente</strong>; ao reabrir, o aviso <em>"Avaliação em andamento"</em> retoma de onde parou.</li>
                        <li><strong>Finalize e consulte</strong> — ao finalizar, o sistema calcula a conformidade e gera o <strong>PDF</strong>. A avaliação passa a aparecer na lista, onde dá para abrir o detalhe por área e exportar em PDF ou Excel.</li>
                    </ol>
                    <div style={{ backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #475569', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#334155', margin: 0 }}>
                            <strong>Como o resultado é decidido</strong><br />
                            Conformidade = soma dos pesos das perguntas respondidas <strong>"Sim"</strong> ÷ soma dos pesos <strong>aplicáveis</strong>. Pergunta marcada <strong>N/A sai do denominador</strong> — não conta nem a favor nem contra.<br />
                            Classificação: ≥90% <strong>Excelente</strong> · 80–89% <strong>Satisfatório</strong> · 60–79% <strong>Atenção</strong> · &lt;60% <strong>Crítico</strong>.<br />
                            Ao contrário da Auditoria (que mede risco), aqui <strong>quanto maior o percentual, melhor</strong>.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'autoavaliacao',
            icon: ShieldCheck,
            title: 'Operacional: Autoavaliação de Controles',
            color: '#475569',
            desc: 'O gerente avalia os controles financeiros e operacionais da própria loja, uma vez por mês.',
            content: (
                <div>
                    <p style={pStyle}>Diferente da Avaliação BSV, aqui <strong>quem responde é o gerente da loja</strong>, sobre a <strong>própria loja</strong> — não há seletor de loja: ela vem do seu cadastro.</p>
                    <ol style={listStyle}>
                        <li><strong>Abra “Nova autoavaliação”</strong> — o formulário já vem com a sua loja e o mês de referência.</li>
                        <li><strong>Responda as 22 perguntas</strong> como <strong>Sim</strong> ou <strong>Não</strong>. Algumas perguntas (Delivery, transferências e Quiosque) aceitam <strong>N/A</strong>, para o que não existe na sua loja.</li>
                        <li><strong>Em dúvida de como se faz?</strong> Onde houver o botão <em>“Como fazer”</em>, o <strong>vídeo do procedimento abre na própria tela</strong>, ao lado da pergunta — assista e continue respondendo.</li>
                        <li><strong>Marcou “Não”?</strong> Abrem <strong>4 campos obrigatórios</strong>: <em>motivo</em> (qual é o problema), <em>plano de ação</em> (o que será feito), <em>responsável</em> (quem responde pela correção) e <em>prazo</em> (data prevista). Sem os quatro, o formulário não é aceito.</li>
                        <li><strong>Finalize</strong> — o sistema calcula o risco e grava. A autoavaliação passa a aparecer na lista, onde dá para abrir e ver as não conformidades com os planos de ação.</li>
                    </ol>
                    <div style={{ backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #475569', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#334155', margin: 0 }}>
                            <strong>Como o risco é calculado</strong><br />
                            Risco = nº de respostas <strong>“Não”</strong> ÷ nº de perguntas <strong>aplicáveis</strong> × 100. Pergunta marcada <strong>N/A sai da conta</strong> — não conta a favor nem contra.<br />
                            Faixas: até 10% 🟢 <strong>Baixo</strong> · &gt;10% até 25% 🟡 <strong>Médio</strong> · &gt;25% até 45% 🟠 <strong>Crítico</strong> · &gt;45% 🔴 <strong>Alto</strong>.<br />
                            Com as 22 perguntas aplicáveis: <strong>0 a 2</strong> “Não” = Baixo · <strong>3 a 5</strong> = Médio · <strong>6 a 9</strong> = Crítico · <strong>10 ou mais</strong> = Alto.<br />
                            Ao contrário da Avaliação BSV (que mede conformidade), aqui <strong>quanto maior o percentual, pior</strong>.
                        </p>
                    </div>
                    <div style={{ backgroundColor: '#fffbeb', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #d97706', marginTop: '12px' }}>
                        <p style={{ fontSize: '13px', color: '#78350f', margin: 0 }}>
                            <strong>É uma por loja, por mês — e não dá para editar depois</strong><br />
                            Ao finalizar, a autoavaliação do mês está entregue. Se algo foi respondido errado, abra-a na lista e use <strong>“Cancelar e responder de novo”</strong>, informando o motivo: a resposta anterior <strong>não é apagada</strong> (fica registrada como cancelada, com o seu nome) e o mês volta a ficar disponível.
                        </p>
                    </div>
                </div>
            )
        },
        {
            // DEM-00011 (Fase 4, T21, 2026-09-23): mudou do módulo Auditoria para
            // cá ("Auditoria Financeira e de Estoques") — conteúdo reescrito na
            // mudança, não só copiado, porque a versão antiga ainda descrevia o
            // método de RISCO anterior a 2026-09-04 (já teria envelhecido duas vezes).
            id: 'checklist-operacional',
            icon: ClipboardCheck,
            title: 'Operacional: Auditoria Financeira e de Estoques',
            color: '#475569',
            desc: 'Checklist de auditoria financeira e de estoques por loja.',
            content: (
                <div>
                    <p style={pStyle}>O card tem <strong>duas abas</strong>: "Auditoria Financeira e de Estoques" (as visitas atuais, método Contagem) e "Histórico" (as 28 antigas, método Peso, só leitura) — mesmo padrão da Avaliação 360 × Histórico BSV.</p>
                    <ul style={listStyle}>
                        <li><strong>Nova Auditoria</strong> (botão no topo da 1ª aba) abre a lista de checklists publicados para preencher.</li>
                        <li><strong>Gerenciar versões</strong> (só Auditoria/Admin) é onde o checklist é montado: o <strong>método</strong> (Peso ou Contagem), as áreas e as perguntas.</li>
                        <li>⚠️ O preenchimento <strong>não</strong> é salvo automaticamente: só existe até você <strong>Finalizar</strong>.</li>
                        <li>As <strong>fotos</strong> anexadas às perguntas negativas aparecem <strong>só no PDF</strong> da auditoria.</li>
                    </ul>
                    <div style={{ backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #475569', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#334155', margin: 0 }}>
                            <strong>Como o resultado é decidido</strong> — depende do <strong>método</strong> da versão usada (a lista de checklists publicados mostra qual é):<br />
                            <strong>Peso</strong> (aba Histórico): pergunta que atende soma o <strong>peso</strong> dela; % = soma ÷ soma dos pesos <strong>aplicáveis</strong>. Faixas: ≥90% <strong>Excelente</strong> · ≥80% <strong>Satisfatório</strong> · ≥60% <strong>Atenção</strong> · &lt;60% <strong>Crítico</strong>.<br />
                            <strong>Contagem</strong> (aba atual, a mesma régua da Avaliação 360): cada pergunta vale o <strong>mesmo</strong> — o peso é ignorado; % = Sim ÷ (Sim+Não). Faixas: ≥90% <strong>Verde</strong> · ≥80% <strong>Amarelo</strong> · ≥70% <strong>Laranja</strong> · &lt;70% <strong>Vermelho</strong>.<br />
                            Nos dois métodos, pergunta marcada <strong>N/A sai do denominador</strong> — não conta a favor nem contra. Auditorias antigas <strong>não são recalculadas</strong> quando um método novo entra.
                        </p>
                    </div>
                </div>
            )
        },
        {
            // DEM-00011 (Fase 4, T22, 2026-09-23): mudou do módulo Treinamento
            // para cá ("Avaliação de Segurança do Alimento") e VOLTOU A SER
            // PREENCHIDA — estava sem escrita desde 09/06/2026 (decisão
            // operacional, não trava técnica: ninguém mais preenchia pelo
            // portal). Conteúdo trazido do verbete antigo (`auditoria-sa`,
            // Treinamento) e corrigido: vocabulário do resultado (era "Aprovado/
            // Reprovado", o real é APROVADA/IMPUR) e o rótulo do export (era
            // "Excel", o botão diz "CSV").
            id: 'avaliacaosa-operacional',
            icon: ShieldAlert,
            title: 'Operacional: Avaliação de Segurança do Alimento',
            color: '#475569',
            desc: 'Auditoria mensal de segurança alimentar por loja, com veto por questão crítica.',
            content: (
                <div>
                    <p style={pStyle}>O card tem <strong>duas abas</strong>: "Avaliação de Segurança do Alimento" (as visitas atuais, método Contagem) e "Histórico" (as antigas, método Peso, só leitura) — mesmo padrão da Avaliação 360 × Histórico BSV e do Checklist × Histórico.</p>
                    <p style={pStyle}><strong>Como preencher:</strong></p>
                    <ol style={listStyle}>
                        <li>Clique em <strong>"Preencher Avaliação"</strong>, no topo do card.</li>
                        <li>Selecione a Loja e a Data, e informe os nomes do Gerente de Negócios (Unidade) e Gerente de Plantão.</li>
                        <li>Responda ao questionário avaliando os critérios técnicos: <em>Higiene e Limpeza, Prevenção contra Contaminação, Armazenamento, Culinária e Geral</em>.</li>
                        <li>Ao final, o sistema mostra o <strong>Resumo</strong> com a pontuação total e o percentual — o resultado (aprovada ou reprovada) só sai depois de enviar.</li>
                        <li>Clique em <strong>"Enviar Auditoria"</strong> para salvar o registro.</li>
                    </ol>
                    <p style={pStyle}><strong>Toda resposta "Não" exige evidência:</strong> ao marcar <strong>Não</strong>, a pergunta abre o bloco de evidência e pede <strong>uma foto e uma observação obrigatórias</strong> (dá para anexar até 2 fotos extras). Vale para <em>todas</em> as perguntas, inclusive as críticas que reprovam a loja — o registro sem prova não é aceito.</p>
                    <p style={pStyle}><strong>PDF da visita:</strong> ao enviar, o sistema gera o PDF da auditoria já com as fotos embutidas e o guarda no SharePoint. Na consulta, o botão <strong>"PDF"</strong> abre esse arquivo — e, para visitas antigas (sem foto), o PDF é gerado na hora.</p>
                    <div style={{ backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #475569', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#334155', margin: 0 }}>
                            <strong>Dois resultados, não um</strong> — a <strong>cor</strong> (Verde/Amarelo/Laranja/Vermelho) mede o desempenho do conjunto; o <strong>veredito</strong> (APROVADA/IMPUR) é um veto à parte: qualquer questão crítica respondida "Não", ou nota abaixo de 80%, reprova a visita mesmo que a cor esteja boa. A tela e o PDF mostram os dois, com a causa do IMPUR nomeada.
                        </p>
                    </div>
                    <p style={pStyle}><strong>Análise de Desempenho (Relatório):</strong> a lista tem filtros ao vivo (loja, método, classificação, status, período) e uma legenda das faixas — clique numa linha para ver o <strong>resumo por área</strong>, ou em <strong>"Exportar Excel"</strong> para baixar a planilha com o que estiver filtrado.</p>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // 12. MÓDULO COMPRAS (dourado)
    // ----------------------------------------------------------------------
    // ------------------------------------------------------------------
    // MÓDULO LOGÍSTICA (2026-09-10)
    // ------------------------------------------------------------------
    // ⚠️ O módulo precisa de um array PRÓPRIO, ainda que com um verbete só. Sem
    //    o `else if` correspondente lá embaixo, `modulo="logistica"` cairia no
    //    `else` — que junta os verbetes do portal INTEIRO — e o Manual da Tela
    //    do Logística mostraria RH, Fiscal e Contábil.
    const manualLogistica = [
        {
            id: 'visao-geral-logistica',
            icon: Truck,
            title: 'Logística: Visão Geral',
            color: '#0e7490',
            desc: 'O que sai da unidade, o comprovante e o retorno.',
            content: (
                <div>
                    <p style={pStyle}>O Módulo de Logística cuida do que se <strong>move fisicamente</strong> entre as unidades e o escritório. Hoje ele tem um programa: a <strong>Retirada de Equipamento</strong>.</p>
                    <p style={pStyle}>Até 09/09/2026 a retirada vivia em dois lugares — um card na Manutenção e uma aba nos Chamados. As duas viraram <strong>esta</strong> tela: é o mesmo assunto, e agora tem um lugar só.</p>
                </div>
            )
        },
        {
            id: 'logistica-retirada',
            icon: PackageOpen,
            title: 'Logística: Retirada de Equipamento',
            color: '#0e7490',
            desc: 'Registrar a saída de um equipamento e acompanhar o retorno.',
            content: (
                <div>
                    <p style={pStyle}>Use esta tela quando um equipamento precisar <strong>sair da unidade</strong> — para conserto, substituição ou descarte.</p>
                    <p style={pStyle}><strong>Ao registrar você informa:</strong></p>
                    <ul style={listStyle}>
                        <li><strong>Equipe</strong> — T.I ou Manutenção. É ela que decide de qual cadastro o equipamento é escolhido.</li>
                        <li><strong>Unidade</strong> — de onde o equipamento sai. O gerente vê só a própria.</li>
                        <li><strong>Equipamento</strong> — escolhido no cadastro. Na Manutenção, se o equipamento não estiver lá, dá para digitar código e nome (o cadastro dela cobre chapas e fritadeiras, não forno nem mobiliário). No T.I não há essa saída: o cadastro cobre o parque inteiro.</li>
                        <li><strong>Motivo</strong> e, se quiser, uma <strong>foto</strong>.</li>
                    </ul>
                    <p style={pStyle}>Ao gravar, o portal emite o <strong>comprovante em PDF</strong> — o papel que acompanha o equipamento, com o bloco de assinatura de quem entrega e de quem retira. Ele pode ser reimpresso a qualquer momento pelo botão na linha da retirada.</p>
                    <div style={{ backgroundColor: '#ecfeff', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #0e7490', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#155e75', margin: 0 }}>
                            <strong>Aqui se acompanha, não se move.</strong> Depois de registrada, a retirada segue no módulo <strong>Compras</strong>: é lá que ela é lançada no SAP, o Fiscal informa a nota, o equipamento é despachado e o retorno é registrado.
                        </p>
                    </div>
                </div>
            )
        },
    ];

    // ------------------------------------------------------------------
    // MÓDULO MARKETING (2026-09-16)
    // ------------------------------------------------------------------
    // ⚠️ Array PRÓPRIO, como todo módulo: sem o `else if` lá embaixo,
    //    `modulo="marketing"` cairia no `else` e o Manual da Tela mostraria os
    //    verbetes do portal inteiro.
    const manualMarketing = [
        {
            id: 'visao-geral-marketing',
            icon: Megaphone,
            title: 'Marketing: Visão Geral',
            color: '#db2777',
            desc: 'As artes que a rede publica, geradas no padrão do grupo.',
            content: (
                <div>
                    <p style={pStyle}>O Módulo de Marketing reúne as ferramentas que <strong>geram arte pronta para publicar</strong>, usando os dados que o portal já tem. Hoje são duas:</p>
                    <ul style={listStyle}>
                        <li><strong>Arte de Contratação:</strong> o anúncio de vagas da loja, em formato Stories.</li>
                        <li><strong>Aniversariantes do Mês:</strong> uma arte A4 por filial, com quem faz aniversário no mês.</li>
                    </ul>
                    <p style={pStyle}>As duas <strong>terminam no arquivo baixado</strong>. O portal não publica em rede social nem envia por WhatsApp — quem posta é você.</p>
                </div>
            )
        },
        {
            id: 'marketing-arte-contratacao',
            icon: Palette,
            title: 'Marketing: Arte de Contratação',
            color: '#db2777',
            desc: 'A arte de "estamos contratando" da loja, pronta para postar.',
            content: (
                <div>
                    <p style={pStyle}>Escolha a <strong>loja</strong>, marque as <strong>vagas abertas</strong> e informe <strong>quem atende o candidato</strong> (nome e telefone). A prévia à direita é exatamente o arquivo que sai ao clicar em <strong>Baixar a arte</strong>.</p>
                    <p style={pStyle}>O <strong>endereço vem preenchido</strong> pela loja escolhida e continua editável — dá para corrigir um complemento ou anunciar uma unidade que ainda não esteja na lista.</p>
                    <div style={{ backgroundColor: '#fdf2f8', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #db2777', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#9d174d', margin: 0 }}>
                            <strong>Texto muito longo é cortado com "…".</strong> A arte tem espaço fixo para cada informação; quando não cabe, o corte aparece — de propósito, para ninguém publicar um endereço sem a cidade sem perceber.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'marketing-aniversariantes',
            icon: Cake,
            title: 'Marketing: Aniversariantes do Mês',
            color: '#db2777',
            desc: 'Uma arte A4 por filial, montada com o cadastro — sem planilha.',
            content: (
                <div>
                    <p style={pStyle}>Escolha o <strong>mês</strong>: o portal monta uma arte por filial com quem está <strong>ativo no cadastro</strong> e faz aniversário naquele mês. Não é preciso enviar planilha nem conferir colunas.</p>
                    <p style={pStyle}><strong>Antes de baixar você pode:</strong></p>
                    <ul style={listStyle}>
                        <li><strong>Ocultar alguém</strong> — quem pediu para não aparecer.</li>
                        <li><strong>Corrigir o cargo</strong> — o cadastro guarda o cargo formal, e a arte às vezes quer o nome que a equipe usa.</li>
                    </ul>
                    <p style={pStyle}>Os ajustes valem <strong>só para a arte</strong>: nada volta para o cadastro. Baixe filial por filial (PNG ou PDF) ou tudo de uma vez (ZIP com os PNGs, ou um PDF com todas as páginas).</p>
                    <div style={{ backgroundColor: '#fdf2f8', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #db2777', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#9d174d', margin: 0 }}>
                            <strong>O gerente de negócios aparece duas vezes</strong> — na arte da loja dele e na do Escritório. É intencional: as duas equipes o cumprimentam.
                        </p>
                    </div>
                </div>
            )
        },
    ];

    const manualCompras = [
        {
            id: 'visao-geral-compras',
            icon: ShoppingCart,
            title: 'Compras: Visão Geral',
            color: '#e3a81d',
            desc: 'Como navegar pelo Módulo de Compras.',
            content: (
                <div>
                    <p style={pStyle}>O Módulo de Compras tem duas telas:</p>
                    <ul style={listStyle}>
                        <li><strong>Solicitações de Compra:</strong> as peças a comprar levantadas nos diagnósticos da Manutenção, com andamento por peça.</li>
                        <li><strong>Catálogo Central de Pedidos:</strong> as peças compráveis pela Central, atualizadas por planilha.</li>
                    </ul>
                    <p style={pStyle}><strong>Compras e Admin operam as duas telas.</strong> A <strong>Manutenção</strong> mantém o <strong>catálogo</strong> (é ela quem descobre a peça no Diagnóstico) e <strong>acompanha</strong> as solicitações — quem marca a peça como comprada ou entregue é o setor de Compras. O <strong>Gerente</strong> acompanha as solicitações da própria loja; a Diretoria, as de toda a rede.</p>
                </div>
            )
        },
        {
            id: 'compras-solicitacoes',
            icon: PackageCheck,
            title: 'Compras: Solicitações de Compra',
            color: '#e3a81d',
            desc: 'Controle peça a peça: nº do pedido no B1, compra, prazo e entrega.',
            content: (
                <div>
                    <p style={pStyle}><strong>Ninguém abre uma solicitação aqui.</strong> Cada peça chega sozinha quando um <strong>Diagnóstico da Certificação</strong> (Manutenção) é finalizado com peças a comprar — a lista chega pronta, sem redigitação.</p>
                    <p style={pStyle}>A tela é uma <strong>tabela de peças</strong> — uma linha por peça, com loja, nome, código, fornecedor, quantidade, valor e o nº do pedido. <strong>Clique na linha</strong> para ver de qual diagnóstico ela veio (equipamento, patrimônio, motivo) e a linha do tempo do andamento.</p>
                    <p style={pStyle}><strong>O caminho de cada peça tem quatro estados:</strong></p>
                    <ul style={listStyle}>
                        <li><strong>Pendente</strong> → a peça foi pedida e ninguém agiu ainda.</li>
                        <li><strong>Pedido lançado</strong> → o <strong>gerente da loja</strong> lançou o pedido no <strong>B1</strong> e informou o número aqui. É o que mostra ao setor de Compras que o pedido saiu.</li>
                        <li><strong>Comprada</strong> → o setor de <strong>Compras</strong> registra a <strong>data da compra</strong>, o <strong>valor unitário</strong> e o <strong>prazo de entrega</strong>. Este botão só aparece <strong>depois</strong> que o número do pedido é informado — é essa a ordem do processo.</li>
                        <li><strong>Entregue</strong> → a peça chegou; informe a <strong>data da entrega</strong>. (<strong>Cancelada</strong> exige o motivo.)</li>
                    </ul>
                    <p style={pStyle}><strong>Informando o nº do pedido:</strong> use o botão <strong>Nº pedido</strong> na linha da peça — ou marque <strong>várias peças</strong> nas caixinhas e informe o mesmo número de uma vez, que é o caso comum (um pedido do B1 cobre todas as peças). Apagar o número desfaz o lançamento.</p>
                    <p style={pStyle}>Os <strong>selos coloridos</strong> no topo contam quantas peças estão em cada estado e servem de atalho: clique num deles para filtrar. Há ainda busca (peça, código, fornecedor, equipamento ou nº do pedido) e filtros de loja, fornecedor e mês.</p>
                    <div style={{ backgroundColor: '#fffbeb', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #e3a81d', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                            <strong>O portal não conversa com o B1.</strong> O número do pedido é digitado aqui — ele serve para cruzar os dois sistemas, e não é conferido no SAP.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'compras-catalogo',
            icon: FileSpreadsheet,
            title: 'Compras: Catálogo Central de Pedidos',
            color: '#e3a81d',
            desc: 'As peças compráveis pela Central — planilha, inclusão manual e ativação.',
            content: (
                <div>
                    <p style={pStyle}>O catálogo é o que alimenta as listas de peças do <strong>Diagnóstico</strong> da Manutenção. Três coisas se fazem aqui:</p>
                    <ul style={listStyle}>
                        <li><strong>Atualizar pela planilha:</strong> reenvie o arquivo (.xlsx) da Central de Pedidos — é o mesmo importador da carga inicial.</li>
                        <li><strong>Incluir peça na mão:</strong> código, descrição, fornecedor, família, unidade e preço. A peça manual fica <strong>protegida do reenvio da planilha</strong> (ela nunca está lá).</li>
                        <li><strong>Ativar / desativar:</strong> peça desativada <strong>some das listas do Diagnóstico</strong>, mas continua no sistema.</li>
                    </ul>
                    <p style={pStyle}>A tabela tem busca (ignora acento) e filtros por fornecedor e situação.</p>
                    <p style={pStyle}><strong>Quem mexe aqui:</strong> Compras, Admin e a <strong>Manutenção</strong> — as três com as mesmas ações. Peça nova encontrada no Diagnóstico pode ser cadastrada na hora, sem depender do setor de Compras.</p>
                    <div style={{ backgroundColor: '#fffbeb', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #e3a81d', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                            <strong>Peça nunca se apaga, só se desativa</strong> — o histórico de compras aponta para ela, e apagar quebraria as solicitações antigas.
                        </p>
                    </div>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // 13. CHAMADOS — roxo #6b21a8
    // ----------------------------------------------------------------------
    const manualChamados = [
        {
            id: 'visao-geral-chamados',
            icon: LifeBuoy,
            title: 'Chamados: Visão Geral',
            color: '#6b21a8',
            desc: 'Como o chamado caminha — e por que ele só fecha quando a loja confirma.',
            content: (
                <div>
                    <p style={pStyle}>Este módulo substitui o <strong>Auvo</strong>: o chamado nasce, anda e fecha aqui dentro. Nesta primeira fase, atende os chamados de <strong>TI</strong>.</p>
                    <p style={pStyle}><strong>O chamado passa por quatro mãos:</strong></p>
                    <ul style={listStyle}>
                        <li><strong>1. A loja abre.</strong> Gerente e Gerente de Plantão descrevem o problema e, se ajudar, anexam foto.</li>
                        <li><strong>2. O Gerente de TI faz a triagem.</strong> É ele quem define a <strong>prioridade</strong> e escolhe o <strong>técnico</strong>. Enquanto isso não acontece, o chamado fica em <strong>Aberto</strong>.</li>
                        <li><strong>3. O técnico executa.</strong> Ao terminar, registra <strong>o que foi feito</strong> e anexa <strong>ao menos uma foto</strong> do serviço.</li>
                        <li><strong>4. A loja confirma.</strong> O Gerente de Plantão diz se resolveu — e é isso que <strong>encerra</strong> o chamado.</li>
                    </ul>
                    <p style={pStyle}><strong>Concluído não é Encerrado.</strong> Concluído é o técnico dizendo que resolveu; Encerrado é a loja confirmando. São dois momentos com datas próprias, e é essa diferença que evita o chamado "fechado" que a loja continua cobrando por WhatsApp.</p>
                    <div style={{ backgroundColor: '#faf5ff', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #6b21a8', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#581c87', margin: 0 }}>
                            <strong>Se a loja não responder em 10 dias</strong> (corridos, contados da conclusão), o Gerente de TI pode encerrar por prazo. O registro mostra que foi <strong>por prazo</strong>, e não por confirmação da loja — as duas coisas não se misturam.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'chamados-abrir',
            icon: PlusCircle,
            title: 'Chamados: Abrir chamado',
            color: '#6b21a8',
            desc: 'O que preencher, e por que você não escolhe a prioridade.',
            content: (
                <div>
                    <p style={pStyle}>Abrem chamado o <strong>Gerente</strong> e o <strong>Gerente de Plantão</strong>, sempre para a <strong>própria loja</strong>.</p>
                    <p style={pStyle}><strong>O que preencher:</strong></p>
                    <ul style={listStyle}>
                        <li><strong>Tipo de solicitação</strong> (obrigatório): o que você está pedindo — <strong>Solicitação de Reparo</strong> (algo quebrou; é o caso da grande maioria), <strong>Manutenção Preventiva</strong>, <strong>Instalação</strong>, <strong>Retirada de equipamento</strong> ou <strong>Serviço Setor de Compras</strong>. Não há opção já marcada: escolher é um ato.</li>
                        <li><strong>Equipamento</strong> (opcional): escolha da lista do parque da sua loja. Se não souber qual é, deixe em branco — é melhor em branco do que errado.</li>
                        <li><strong>Assunto:</strong> o problema em poucas palavras.</li>
                        <li><strong>Descrição:</strong> o que está acontecendo, desde quando, e o que já foi tentado.</li>
                        <li><strong>Fotos</strong> (até 3, opcionais): a tela do erro, o cabo solto, o equipamento apagado.</li>
                    </ul>
                    <p style={pStyle}><strong>Tipo não é prioridade.</strong> O tipo diz <em>o que</em> você está pedindo, e é você quem escolhe. A urgência quem define é o TI, na triagem. Se você marcar o tipo errado, o Gerente de TI corrige ali — não precisa abrir outro chamado.</p>
                    <p style={pStyle}><strong>Você não escolhe a prioridade — e isso é de propósito.</strong> Quem define é o Gerente de TI, na triagem, olhando a fila inteira. Descreva bem o impacto ("a loja está sem vender no drive") e a urgência aparece sozinha.</p>
                    <p style={pStyle}>Enquanto ninguém do TI tiver começado, você pode <strong>cancelar</strong> o chamado que abriu. Depois que o técnico entra, o cancelamento passa a ser do TI — o trabalho dele já é registro.</p>
                </div>
            )
        },
        {
            id: 'chamados-consulta',
            icon: ListChecks,
            title: 'Chamados: acompanhamento e fila',
            color: '#6b21a8',
            desc: 'A lista de trabalho: de quem se espera e há quanto tempo está parado.',
            content: (
                <div>
                    <p style={pStyle}>É a <strong>mesma tela para todos</strong> — o que muda são as ações. A loja vê os chamados <strong>da própria loja</strong>; o TI vê a rede inteira.</p>
                    <p style={pStyle}>Três abas: <strong>Em aberto</strong> (tudo que ainda espera alguém, incluindo o que aguarda a confirmação da loja), <strong>Encerrados</strong> e <strong>Todos</strong>.</p>
                    <p style={pStyle}>Duas colunas fazem dela uma lista de trabalho e não um arquivo:</p>
                    <ul style={listStyle}>
                        <li><strong>Aguardando:</strong> de quem se espera a próxima ação — a triagem do TI, um técnico com nome, a loja, ou a confirmação dela.</li>
                        <li><strong>Prazo:</strong> o SLA do chamado, contado da <strong>abertura</strong>. Enquanto ninguém assume, é o prazo de <em>atendimento</em>; depois, o de <em>solução</em>. Ele <strong>pausa</strong> quando o chamado espera a loja ou um terceiro — e aí a coluna mostra a <em>previsão</em> registrada, não uma contagem. Chamado <strong>sem prioridade</strong> não tem prazo (é a triagem que define), e nesses a coluna mostra os <strong>dias parados</strong> desde o último <strong>ato</strong> — que não conta salvamento de sistema, só o que uma pessoa fez.</li>
                    </ul>
                    <p style={pStyle}><strong>O que cada um faz aqui:</strong></p>
                    <ul style={listStyle}>
                        <li><strong>Gerente de TI:</strong> tria (prioridade + técnico), cancela e encerra por prazo quando passar de 10 dias.</li>
                        <li><strong>Técnico:</strong> assume, <em>pausa o atendimento</em> (dizendo o motivo) quando não dá para seguir, e conclui com descrição e foto.</li>
                        <li><strong>Gerente de Plantão:</strong> confirma que resolveu — ou <strong>devolve</strong>, dizendo o que continua acontecendo.</li>
                    </ul>
                    <div style={{ backgroundColor: '#faf5ff', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #6b21a8', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#581c87', margin: 0 }}>
                            <strong>Devolver não é reclamar, é a outra metade da confirmação.</strong> Se não resolveu, diga aqui — o chamado volta para o mesmo técnico com o seu relato anexado, sem precisar abrir outro.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'chamados-retirada',
            icon: Monitor,
            title: 'Chamados: Retirada de equipamento',
            color: '#6b21a8',
            desc: 'A saída de um equipamento da unidade, com comprovante em PDF.',
            content: (
                <div>
                    <p style={pStyle}>É o registro de que um equipamento <strong>vai sair da unidade</strong> — para conserto, substituição ou descarte. Não é chamado: aqui não há defeito a resolver na loja, há um aparelho a transportar.</p>
                    <p style={pStyle}>Ao gravar, o portal faz três coisas:</p>
                    <ul style={listStyle}>
                        <li>emite o <strong>comprovante em PDF</strong> — com a unidade, o nome e o código do equipamento e o motivo — para <strong>acompanhar o equipamento</strong> e ser assinado por quem entrega e por quem retira;</li>
                        <li>avisa por e-mail o <strong>Compras</strong> e a <strong>gestão de TI</strong>;</li>
                        <li>coloca o equipamento na <strong>esteira</strong>, que o Compras e o Fiscal movimentam.</li>
                    </ul>
                    <p style={pStyle}><strong>O equipamento vem do parque de TI</strong> — não se digita o nome. É o cadastro que garante que o mesmo aparelho seja sempre o mesmo aparelho, e é por isso que dá para responder quantas vezes ele já saiu da loja. Se o equipamento não estiver no parque, cadastre-o em <strong>Equipamentos</strong> antes.</p>
                    <p style={pStyle}>A consulta agrupa por onde o equipamento está: <strong>Ainda na unidade</strong> (registrado, mas não saiu), <strong>Fora da unidade</strong> (a caminho ou no fornecedor) e <strong>Retornaram</strong>. A coluna <em>Previsão</em> fica vermelha quando passou da data que o fornecedor prometeu.</p>
                    <div style={{ backgroundColor: '#faf5ff', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #6b21a8', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#581c87', margin: 0 }}>
                            <strong>Aqui você monitora, não movimenta.</strong> Quem dá os passos da esteira é o Compras (e o Fiscal, na nota) — o mesmo fluxo que a Manutenção já usa. O que o T.I controla é abrir a retirada, imprimir o comprovante e acompanhar o retorno.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'chamados-indicadores',
            icon: ListChecks,
            title: 'Chamados: Indicadores de SLA',
            color: '#6b21a8',
            desc: 'Quanto do que a loja abre é atendido dentro do prazo — meta de 90%.',
            content: (
                <div>
                    <p style={pStyle}>Um número manda nesta tela: <strong>% de chamados atendidos dentro do prazo</strong>, com <strong>meta de 90%</strong>. O resto existe para você conferir de onde ele saiu.</p>
                    <p style={pStyle}>Cada chamado tem <strong>dois prazos</strong>, e os dois começam a contar na <strong>abertura</strong> — é o que a loja sente esperando:</p>
                    <ul style={listStyle}>
                        <li><strong>Atendimento:</strong> da abertura até o técnico assumir o chamado.</li>
                        <li><strong>Solução:</strong> da abertura até o serviço estar feito.</li>
                    </ul>
                    <p style={pStyle}>O prazo depende da <strong>prioridade</strong>, definida na triagem:</p>
                    <ul style={listStyle}>
                        <li><strong>🔴 Alta</strong> — 18h para atender, 24h para resolver, contadas <strong>direto, incluindo fim de semana</strong> (impacta venda, não espera segunda).</li>
                        <li><strong>🟡 Média</strong> — 2 e 3 <strong>dias úteis</strong>.</li>
                        <li><strong>🟢 Baixa</strong> — 4 e 7 <strong>dias úteis</strong>.</li>
                    </ul>
                    <p style={pStyle}><strong>O relógio pausa</strong> quando o chamado espera a loja ou alguém de fora (uma peça, o Invgate, um reparo). Nesse momento o técnico registra o motivo e a <strong>previsão de conclusão</strong> — é essa data que passa a ser cobrada. Peça que não chegou não conta como atraso da equipe.</p>
                    <p style={pStyle}><strong>Chamado sem prioridade fica fora do percentual</strong>, e aparece em lista própria: sem triagem não existe prazo a aplicar. Se esse número estiver alto, é a triagem que está devendo — não o atendimento.</p>
                    <div style={{ backgroundColor: '#faf5ff', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #6b21a8', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#581c87', margin: 0 }}>
                            <strong>Chamado aberto que já passou do prazo conta como fora, mesmo sem ninguém ter tocado nele.</strong> É de propósito: se só o que foi atendido entrasse na conta, deixar chamado parado melhoraria o indicador — e é exatamente o contrário do que ele mede.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'chamados-ativos',
            icon: Monitor,
            title: 'Chamados: Equipamentos',
            color: '#6b21a8',
            desc: 'O parque de TI de cada loja — é dele que sai o seletor da abertura.',
            content: (
                <div>
                    <p style={pStyle}>Cada aparelho real da loja é um cadastro: <em>"o POS do balcão 2 da FER"</em>. É esta lista que aparece para a loja escolher ao abrir o chamado.</p>
                    <p style={pStyle}>Mantido pelo <strong>TI</strong> (Gerente de TI e técnicos), que é quem sabe o que existe em cada unidade. Cada equipamento recebe um <strong>código</strong> curto no estilo <code>POS-7K2P</code>, feito para ser ditado ao telefone (sem letras que se confundem, como O e 0).</p>
                    <p style={pStyle}>O parque <strong>já vem carregado</strong> do Auvo: 365 equipamentos de TI, com a loja e a descrição que já eram usadas lá. O que <strong>não</strong> veio foram os itens de prédio que estavam na fila de TI do Auvo (forro, iluminação, boiler, caixa de gordura) — eles são da Manutenção, e no seletor de TI só atrapalhariam.</p>
                    <p style={pStyle}><strong>Família</strong> é o agrupamento (POS, KVS, Impressora, Bumpbar, Câmera…) e vem de uma lista fechada. Se algo não couber em nenhuma, use <strong>Outro</strong> — e avise, porque família nova é sinal de que falta uma opção.</p>
                    <div style={{ backgroundColor: '#faf5ff', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #6b21a8', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#581c87', margin: 0 }}>
                            <strong>Equipamento com chamado no histórico não se apaga — desative.</strong> Desativado, ele sai da lista de abertura e os chamados antigos continuam legíveis. A tela avisa quando é esse o caso.
                        </p>
                    </div>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // 14. HOME (saguão) — vermelho da marca
    // ----------------------------------------------------------------------
    const manualHome = [
        {
            id: 'home-visao-geral',
            icon: Home,
            title: 'Home: Visão Geral',
            color: '#990605',
            desc: 'O que você encontra no saguão do portal.',
            content: (
                <div>
                    <p style={pStyle}>A Home é a tela que abre depois do login. Ela é <strong>uma só</strong>, organizada em <strong>abas</strong> — e quais abas você tem depende do seu perfil:</p>
                    <ul style={listStyle}>
                        <li><strong>Operação</strong> — o dashboard de venda, orçamento, projeção e avaliações. Para Admin, Gerente, Gerente de Plantão, Diretoria e Diretoria de Operação.</li>
                        <li><strong>RH</strong> — a leitura de pessoas (headcount, absenteísmo, quadro real × ideal). Para os mesmos acima e também para RH e Diretoria de RH.</li>
                        <li><strong>Escritório</strong> — o saguão, com as <strong>Atividades pendentes</strong> do seu perfil (agrupadas por módulo e filtráveis por loja) e os <strong>Destaques do Mês</strong>. Para quem é cadastrado no <strong>escritório</strong>. A agenda fica no ícone de calendário do topo, que vale para qualquer perfil.</li>
                    </ul>
                    <p style={pStyle}>A Home abre na <strong>primeira aba</strong> que você tem, e se você tiver só uma a barra nem aparece — em vez dela, um título diz qual painel está na tela.</p>
                    <p style={pStyle}>Na <strong>barra do topo</strong>, sempre disponíveis: o <strong>sino de avisos</strong>, o <strong>calendário / agenda corporativa</strong>, este <strong>manual</strong> e o botão de sair. A busca global ainda está bloqueada ("Em breve").</p>
                    <p style={pStyle}>O <strong>Acesso Rápido</strong> está inteiro em "Em breve". <strong>Colaboradores</strong> e <strong>Indicadores</strong> continuam no <strong>menu lateral</strong> — o que saiu foi o atalho, não o acesso.</p>
                </div>
            )
        },
        {
            id: 'home-dashboard-operacao',
            icon: Gauge,
            title: 'Home: Dashboard de Operação',
            color: '#990605',
            desc: 'Venda, GC e ticket médio da loja comparados com a rede.',
            content: (
                <div>
                    <p style={pStyle}>A visão <strong>Operação</strong> compara sempre a <strong>loja em foco com o mercado</strong> (a média da rede). Filtros de ano, mês e loja no topo — o gerente já entra na própria loja.</p>
                    <ul style={listStyle}>
                        <li><strong>Cards do topo:</strong> <em>Venda</em>, <em>GC</em> (clientes) e <em>Ticket Médio</em> no acumulado do mês, com a variação contra o ano anterior.</li>
                        <li><strong>Projeção e Orçamento:</strong> ao lado da venda, mostram o quanto o realizado ficou <strong>acima ou abaixo</strong> do alvo — a seta segue a direção boa (acima do alvo é verde).</li>
                        <li><strong>Resumo por segmento:</strong> a tabela por canal de venda, no mesmo desenho do Power BI.</li>
                        <li><strong>Avaliações:</strong> a nota mais recente de cada programa (Segurança do Alimento, Treinamento, Auditoria/Checklist, BSV e Certificação) da loja contra a média da rede — com <strong>"Ver PDF"</strong> para abrir o relatório da visita.</li>
                    </ul>
                    <p style={pStyle}>O selo de <strong>atualização dos dados</strong> aparece no cabeçalho: os números de venda vêm do dia anterior.</p>
                </div>
            )
        },
        {
            id: 'home-visao-rh',
            icon: Users,
            title: 'Home: Visão RH do Dashboard',
            color: '#990605',
            desc: 'Headcount, absenteísmo e quadro real × ideal.',
            content: (
                <div>
                    <p style={pStyle}>A aba <strong>RH</strong>, ao lado de Operação, troca o dashboard para a leitura de pessoas — mesmos filtros, mesma comparação com o mercado.</p>
                    <ul style={listStyle}>
                        <li><strong>Cards:</strong> Contratados, Desligados e Turnover; <strong>Faltas</strong>, <strong>Atestados</strong> e o <strong>% de Absenteísmo</strong>, com o desvio contra a rede.</li>
                        <li><strong>Real × Ideal:</strong> quantas pessoas a loja tem por cargo contra o quadro ideal.</li>
                        <li><strong>Experiência:</strong> quem está em contrato de experiência e quando vence.</li>
                        <li><strong>Aniversariantes</strong> do período.</li>
                    </ul>
                    <p style={pStyle}>Faltas e atestados vêm do <strong>espelho de ponto</strong> (a mesma fonte do Power BI), não do módulo de Medicina — por isso os números batem com o BI e não com a Revisão de Atestados, que mede outra coisa.</p>
                </div>
            )
        },
        {
            id: 'home-calendario-agenda',
            icon: CalendarDays,
            title: 'Home: Calendário e Agenda Corporativa',
            color: '#990605',
            desc: 'Feriados, eventos compartilhados e a agenda do Teams.',
            content: (
                <div>
                    <p style={pStyle}>O ícone de <strong>calendário</strong> na barra do topo abre o mês com três coisas juntas:</p>
                    <ul style={listStyle}>
                        <li><strong>Feriados:</strong> valem para a rede toda; só Admin e Marketing cadastram.</li>
                        <li><strong>Eventos:</strong> de um dia ou de um período, <strong>compartilhados por pessoa</strong>. Sem ninguém marcado, o evento é privado. Loja e perfil ali são <strong>filtros para achar gente</strong>, não o alvo do compartilhamento.</li>
                        <li><strong>Agenda Corporativa:</strong> os seus compromissos do <strong>Teams/Microsoft 365</strong>, lidos da sua própria conta (é preciso conectar uma vez).</li>
                    </ul>
                    <p style={pStyle}>Ao criar um evento, o portal <strong>espelha no Teams</strong> e manda <strong>e-mail</strong> a quem foi compartilhado.</p>
                    <div style={{ backgroundColor: '#fef2f2', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #990605', marginTop: '16px' }}>
                        <p style={{ fontSize: '13px', color: '#991b1b', margin: 0 }}>
                            <strong>Quem pode criar:</strong> escritório, <strong>Gerente</strong> e <strong>Gerente Adm</strong>. O <strong>Gerente de Plantão não cria</strong> evento nem compromisso — mas vê os que foram compartilhados com ele.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'home-avisos',
            icon: Bell,
            title: 'Home: Sino de Avisos',
            color: '#990605',
            desc: 'Comunicados do portal, com recorte por perfil e loja.',
            content: (
                <div>
                    <p style={pStyle}>O <strong>sino</strong> na barra do topo mostra os avisos ainda não lidos. Clicar em um deles abre o texto completo; depois de lido, ele sai do contador.</p>
                    <p style={pStyle}>Há dois tipos: os <strong>tutoriais do próprio portal</strong> (como o tour da Home) e os <strong>comunicados de texto cadastrados</strong> pelo Painel Admin — que podem ser <strong>agendados</strong> e direcionados a um <strong>perfil ou loja</strong> específicos. Se um aviso não aparece para você, quase sempre é recorte ou janela de exibição.</p>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // MÓDULO BIBLIOTECA (2026-09-04)
    // ----------------------------------------------------------------------
    // ⚠️ Este é o Manual da Tela DO MÓDULO QUE PUBLICA MANUAIS — e sim, a
    //    recursão é esquisita de ler. Ela existe porque as duas coisas são
    //    diferentes: o módulo publica o DOCUMENTO de processo (como fazer a
    //    sangria) e os VÍDEOS; estes verbetes explicam a TELA que os exibe
    //    (abas, índice, busca, link, impressão).
    const manualBiblioteca = [
        {
            id: 'biblioteca-visao-geral',
            icon: BookOpen,
            title: 'Biblioteca: Visão Geral',
            color: '#92400e',
            desc: 'As duas prateleiras do módulo — Manuais e Videoteca — e como achar um passo sem ler 82 páginas.',
            content: (
                <div>
                    <p style={pStyle}>Este módulo guarda o material de consulta do Grupo Correia dentro do portal, em <strong>duas abas</strong>: <strong>Manuais</strong> (os documentos de processo) e <strong>Videoteca</strong> (os vídeos de procedimento). O primeiro manual é o <strong>Manual de Processos Financeiros</strong>, que descreve a rotina de caixa do restaurante: abertura do dia, abertura de operador, caixa surpresa, sangrias, cancelamento de cupom, Petty Cash, troco, estorno, nulos, Prosegur, caderno financeiro e fechamento do dia.</p>
                    <p style={pStyle}><strong>Cada capítulo tem sempre a mesma estrutura</strong>, que é a do documento original:</p>
                    <ul style={listStyle}>
                        <li><strong>Objetivo</strong> — para que serve o procedimento.</li>
                        <li><strong>Responsável</strong> — quem executa (na maioria, o Gerente de Plantão).</li>
                        <li><strong>Quando realizar</strong> — o momento da rotina.</li>
                        <li><strong>Como fazer</strong> — os passos numerados, cada um com o print da tela.</li>
                        <li><strong>Avisos</strong> — em amarelo o que exige cuidado, em vermelho o que não se pode fazer.</li>
                        <li><strong>Checklist de conclusão</strong> — o que tem de estar pronto no fim.</li>
                    </ul>
                    <p style={pStyle}><strong>Não é um PDF anexado.</strong> Por isso dá para <strong>buscar</strong>, <strong>ampliar os prints</strong> e <strong>mandar o link de um capítulo</strong> — coisas que o arquivo no WhatsApp não permite.</p>
                    <p style={pStyle}>O módulo se chamava <strong>Manuais</strong> até 04/09/2026. Virou <strong>Biblioteca</strong> quando ganhou a Videoteca: com vídeo dentro, o nome antigo passou a descrever só metade do acervo. Links antigos continuam funcionando.</p>
                </div>
            )
        },
        {
            id: 'biblioteca-como-usar',
            icon: Search,
            title: 'Buscar, ampliar e compartilhar',
            color: '#92400e',
            desc: 'Os três recursos que resolvem o problema do PDF de 82 páginas.',
            content: (
                <div>
                    <p style={pStyle}><strong>A busca olha o manual inteiro</strong>, não só os títulos: procure por <em>banana</em>, <em>malote</em>, <em>testemunha</em> ou <em>lacre</em> e o índice mostra em quais capítulos a palavra aparece. Acento não importa — “atencao” acha “atenção”.</p>
                    <p style={pStyle}><strong>Clique em qualquer print para ampliar.</strong> As telas do POS e do FLEX têm texto miúdo; no tamanho reduzido do capítulo boa parte fica ilegível, principalmente no celular. O zoom fecha com <strong>Esc</strong> ou clicando fora.</p>
                    <p style={pStyle}><strong>O endereço da página acompanha o capítulo aberto.</strong> Copie a URL da barra do navegador e mande no grupo da loja: quem clicar cai direto naquele capítulo, e não no começo do manual.</p>
                    <p style={pStyle}><strong>Imprimir</strong> usa a impressão do navegador e sai o capítulo que está na tela — útil para deixar o procedimento na parede da gerência.</p>
                    <p style={pStyle}>No fim de cada capítulo há <strong>Anterior</strong> e <strong>Próximo</strong>, para quem está lendo o manual de ponta a ponta em vez de consultar um passo.</p>
                </div>
            )
        },
        {
            id: 'biblioteca-vs-manual-da-tela',
            icon: HelpCircle,
            title: 'Manuais x Manual da Tela',
            color: '#92400e',
            desc: 'São duas ajudas diferentes, e saber qual abrir economiza tempo.',
            content: (
                <div>
                    <p style={pStyle}>O portal tem <strong>duas</strong> ajudas, e elas não se substituem:</p>
                    <ul style={listStyle}>
                        <li><strong>Manual da Tela</strong> (este painel, o “?” do cabeçalho) — ajuda <strong>curta e contextual</strong>, dentro da tela onde você está trabalhando. Responde “o que é este campo?”, “o que esta aba faz?”.</li>
                        <li><strong>Biblioteca</strong> (o módulo na barra lateral) — o <strong>documento de processo completo</strong>, com passos, prints e checklist. Responde “como se faz a sangria total, do começo ao fim”, inclusive a parte que acontece fora do portal (no POS, no FLEX, no papel).</li>
                    </ul>
                    <p style={pStyle}>Regra prática: se a dúvida é <strong>sobre uma tela do portal</strong>, use o “?” daquela tela. Se a dúvida é <strong>sobre o procedimento</strong>, vá à Biblioteca.</p>
                    <p style={pStyle}>Quatro capítulos do Manual de Processos Financeiros passam pelo portal — <strong>Estorno de Venda</strong>, <strong>Nulos em Progresso</strong>, <strong>Petty Cash</strong> e <strong>Troco</strong> —, e nesses casos vale ler os dois: o manual mostra o processo inteiro, e o “?” da tela detalha os campos.</p>
                </div>
            )
        },
        {
            id: 'biblioteca-print-nao-aparece',
            icon: FileWarning,
            title: 'Um print não aparece',
            color: '#92400e',
            desc: 'O que significa “Imagem indisponível” e como resolver.',
            content: (
                <div>
                    <p style={pStyle}>Os prints deste módulo <strong>só abrem para quem está logado</strong>. Isso é de propósito: várias telas do manual mostram <strong>nome de colaborador, matrícula, assinatura e valor de malote</strong>, e esse conteúdo não pode ficar acessível a quem não tem acesso ao portal.</p>
                    <p style={pStyle}>Consequência prática: a autorização das imagens <strong>vence com o tempo</strong>. A tela renova sozinha enquanto está aberta, mas se o computador ficou suspenso ou a aba passou a noite aberta, pode aparecer <strong>“Imagem indisponível”</strong>.</p>
                    <p style={pStyle}><strong>Conserto:</strong> recarregue a página (F5, ou Ctrl+Shift+R). Se continuar depois de recarregar, avise a equipe de dados — aí é o arquivo que está faltando, não a autorização.</p>
                    <p style={pStyle}>⚠️ Por isso também <strong>não funciona copiar o endereço de uma imagem</strong> e mandar por WhatsApp: o link morre. Mande o <strong>link do capítulo</strong> (a URL da barra do navegador).</p>
                </div>
            )
        },
        {
            id: 'biblioteca-videoteca',
            icon: Video,
            title: 'Videoteca',
            color: '#92400e',
            desc: 'Os vídeos de procedimento reunidos — os mesmos que aparecem dentro das telas.',
            content: (
                <div>
                    <p style={pStyle}>A aba <strong>Videoteca</strong> reúne os <strong>vídeos de procedimento</strong> do portal. Não são vídeos novos: são exatamente os mesmos que já aparecem no botão <em>“Como fazer”</em> dentro das telas de trabalho — hoje o <strong>Lançamento de Petty Cash</strong> e o <strong>Registro de Nulos em Progresso</strong>, os dois no Financeiro.</p>
                    <p style={pStyle}><strong>Para que serve ter os dois lugares:</strong> dentro da tela o vídeo serve a quem está executando a tarefa e travou num passo. Aqui ele serve a quem quer assistir <strong>sem estar no meio do trabalho</strong> — gerente novo aprendendo a rotina, treinamento de turno, ou conferir como o procedimento deve ser feito antes de cobrar.</p>
                    <p style={pStyle}>Cada card mostra <strong>de que módulo</strong> o vídeo é e <strong>em que tela</strong> o procedimento acontece. O vídeo abre <strong>na própria página</strong>, sem baixar arquivo e sem abrir aba nova.</p>
                    <p style={pStyle}><strong>Se um vídeo não carregar:</strong> a autorização do player vale <strong>10 minutos</strong>. Com a aba aberta há muito tempo, o play pode falhar — clique em <strong>“Tentar de novo”</strong> ou recarregue a página. Se persistir, avise a equipe de dados.</p>
                    <p style={pStyle}>A lista <strong>se atualiza sozinha</strong>: vídeo novo gravado pela equipe aparece aqui sem precisar de atualização do portal. Se a aba estiver vazia, é porque ainda não há vídeo publicado — não é erro.</p>
                </div>
            )
        }
    ];

    // ----------------------------------------------------------------------
    // Roteamento por módulo
    // ----------------------------------------------------------------------
    // Cada casco passa `modulo` para ver só os verbetes do seu módulo. Sem
    // `modulo`, junta TUDO (else) — é o caminho usado pelas telas que abrem o
    // manual com `showOnlyIds` (precisam que o id exista em algum array).
    let baseManuais = [];

    // ⚠️ O caso `modulo === 'auditoria'` saiu em 2026-09-23 (DEM-00011, Fase 5,
    // T26) junto com o hub — não existe mais tela que passe esse `modulo`.
    if (modulo === 'treinamento') {
        baseManuais = manualTreinamento;
    } else if (modulo === 'medicina') {
        baseManuais = manualMedicina;
    } else if (modulo === 'processo-seletivo') {
        baseManuais = manualProcessoSeletivo;
    } else if (modulo === 'dados') {
        baseManuais = manualDados;
    } else if (modulo === 'fiscal') {
        baseManuais = manualFiscal;
    } else if (modulo === 'contabil') {
        baseManuais = manualContabil;
    } else if (modulo === 'financeiro') {
        baseManuais = manualFinanceiro;
    } else if (modulo === 'rh') {
        baseManuais = manualRh;
    } else if (modulo === 'mapa-caixa') {
        baseManuais = manualMapaCaixa;
    } else if (modulo === 'manutencao') {
        baseManuais = manualManutencao;
    } else if (modulo === 'operacional') {
        baseManuais = manualOperacional;
    } else if (modulo === 'compras') {
        baseManuais = manualCompras;
    } else if (modulo === 'chamados') {
        baseManuais = manualChamados;
    } else if (modulo === 'biblioteca') {
        baseManuais = manualBiblioteca;
    } else if (modulo === 'logistica') {
        baseManuais = manualLogistica;
    } else if (modulo === 'marketing') {
        baseManuais = manualMarketing;
    } else if (modulo === 'home') {
        baseManuais = manualHome;
    } else {
        // Sem módulo definido: junta tudo (comportamento padrão seguro).
        baseManuais = [
            ...manualHome,
            ...manualMarketing,
            ...manualTreinamento,
            ...manualMedicina,
            ...manualProcessoSeletivo,
            ...manualDados,
            ...manualFiscal,
            ...manualContabil,
            ...manualFinanceiro,
            ...manualRh,
            ...manualMapaCaixa,
            ...manualManutencao,
            ...manualOperacional,
            ...manualCompras,
            ...manualLogistica,
            ...manualChamados,
            ...manualBiblioteca,
        ];
    }

    // Se a tela enviou IDs específicos (ex: abriu direto de dentro de um app), mostra só eles. Senão, mostra os permitidos do módulo.
    const manuaisPermitidos = showOnlyIds
        ? baseManuais.filter(item => showOnlyIds.includes(item.id))
        : baseManuais;

    // Filtra pelo termo buscado na barra de pesquisa
    const conteudosFiltrados = manuaisPermitidos.filter(item =>
        item.title.toLowerCase().includes(termoBusca.toLowerCase()) ||
        item.desc.toLowerCase().includes(termoBusca.toLowerCase())
    );

    return (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, backgroundColor: 'rgba(15, 23, 42, 0.4)', zIndex: 9999, display: 'flex', justifyContent: 'flex-end', animation: 'fadeIn 0.2s ease-out' }}>
            <div style={{ position: 'absolute', width: '100%', height: '100%' }} onClick={onClose} />

            <div style={{ width: '100%', maxWidth: '450px', backgroundColor: '#f8fafc', height: '100vh', overflowY: 'auto', boxShadow: '-10px 0 25px rgba(0,0,0,0.1)', position: 'relative', display: 'flex', flexDirection: 'column' }}>

                <div style={{ padding: '24px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ backgroundColor: '#e0f2fe', color: '#0ea5e9', padding: '10px', borderRadius: '12px' }}>
                            <BookOpen size={24} />
                        </div>
                        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '900', color: '#0f172a' }}>Central de Ajuda</h2>
                    </div>
                    <button onClick={onClose} style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', color: '#64748b', padding: '8px', borderRadius: '50%', display: 'flex' }}>
                        <X size={20} />
                    </button>
                </div>

                <div style={{ padding: '24px', flex: 1 }}>
                    <div style={{ position: 'relative', marginBottom: '24px' }}>
                        <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
                        <input
                            type="text"
                            placeholder="Pesquise uma dúvida ou módulo..."
                            value={termoBusca}
                            onChange={(e) => setTermoBusca(e.target.value)}
                            style={{ width: '100%', padding: '14px 16px 14px 44px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', color: '#0f172a', boxSizing: 'border-box' }}
                        />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {conteudosFiltrados.length > 0 ? (
                            conteudosFiltrados.map((item) => {
                                // MÁGICA AQUI: Pega a cor específica do item ou usa o azul padrão
                                const activeColor = item.color || '#0ea5e9';

                                return (
                                    <div key={item.id} style={{
                                        backgroundColor: '#fff',
                                        borderRadius: '12px',
                                        border: secaoAtiva === item.id ? `2px solid ${activeColor}` : '1px solid #e2e8f0',
                                        overflow: 'hidden',
                                        transition: 'all 0.2s',
                                        // Aplica uma sombra suave com a cor específica (adicionando '25' para transparência no hex)
                                        boxShadow: secaoAtiva === item.id ? `0 4px 12px ${activeColor}25` : 'none'
                                    }}>
                                        <button onClick={() => toggleSecao(item.id)} style={{ width: '100%', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <item.icon size={20} color={secaoAtiva === item.id ? activeColor : '#64748b'} />
                                                <div>
                                                    <h3 style={{ fontSize: '15px', fontWeight: '800', color: secaoAtiva === item.id ? activeColor : '#0f172a', margin: 0 }}>
                                                        {item.title}
                                                    </h3>
                                                </div>
                                            </div>
                                            <div style={{ color: secaoAtiva === item.id ? activeColor : '#94a3b8' }}>
                                                {secaoAtiva === item.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                            </div>
                                        </button>
                                        {secaoAtiva === item.id && (
                                            <div style={{ padding: '0 16px 20px 16px' }}>
                                                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                                                    {item.content}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                Nenhum resultado encontrado para "{termoBusca}".
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const pStyle = { fontSize: '13px', color: '#334155', margin: '0 0 10px 0', lineHeight: '1.5' };
const listStyle = { fontSize: '13px', color: '#334155', margin: 0, paddingLeft: '20px', lineHeight: '1.6' };

export default HelpCenter;
